<template>
  <div class="playground-container">
    <!-- Notification -->
    <div 
      v-if="notification.show" 
      class="notification"
      :class="notification.type"
    >
      {{ notification.message }}
    </div>

    <!-- Back button -->
    <button class="back-button" @click="goBack">
      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
        <path d="M19 12H5M12 19l-7-7 7-7"/>
      </svg>
      Retour
    </button>

    <!-- Sidebar -->
    <div class="sidebar">
      <div class="sidebar-header">
        <div class="header-title">
          <div class="logo">
            <img src="https://peelocar.mojay.pro/assets/img/logoblanc.png" alt="Peelo Logo" />
          </div>
          <button class="add-btn" @click="openCreateBlockModal" @mouseenter="showTooltip" @mouseleave="hideTooltip" title="Créer un nouveau bloc">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <line x1="12" y1="5" x2="12" y2="19"></line>
              <line x1="5" y1="12" x2="19" y2="12"></line>
            </svg>
            <span class="tooltip-text">Créer un nouveau bloc</span>
          </button>
        </div>
        
        <!-- Informations du chatbot -->
        <div class="chatbot-info" v-if="chatbot && chatbot.bot_name">
          <router-link :to="`/dashboard/chatbots/${$route.params.idchatbot}/home`" class="chatbot-name">
            {{ chatbot.bot_name }}
          </router-link>
          <p class="chatbot-description" v-if="chatbot.description">
            {{ chatbot.description }}
          </p>
        </div>

        <div class="create-variable" v-if="chatbot">
          <router-link :to="`/dashboard/chatbots/${$route.params.idchatbot}/variables`" class="create-variable-btn-link">
            <button class="create-variable-btn" >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                <path d="M12 5v14M5 12h14"/>
              </svg>
              Créer une variable
            </button>
          </router-link>
        </div>

        
        <div class="search-container">
          <div class="search-input">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <circle cx="11" cy="11" r="8"></circle>
              <line x1="21" y1="21" x2="16.65" y2="16.65"></line>
            </svg>
            <input 
              type="text" 
              placeholder="Rechercher un bloc..." 
              v-model="searchQuery" 
              @input="filterBlocks"
            />
          </div>
        </div>
      </div>

      <div class="expanded-sidebar">
        <div v-if="loading" class="loading-state">
          <div class="loading-spinner">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <circle cx="12" cy="12" r="10"></circle>
            </svg>
          </div>
          <p>Chargement des flux...</p>
        </div>

        <div v-else-if="error" class="error-state">
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <circle cx="12" cy="12" r="10"/>
            <path d="M12 8v4M12 16h.01"/>
          </svg>
          <p>{{ error }}</p>
          <button @click="initializeData" class="retry-btn">Réessayer</button>
        </div>

        <div v-else>
          <!-- Main Flows Section -->
          <div class="folder-section">
            <div class="folder-header" @click="toggleFolder('welcome')">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" :class="{ 'rotated': !foldersState.welcome }">
                <polyline points="6 9 12 15 18 9"></polyline>
              </svg>
              <span>Flux Principaux</span>
            </div>
            <div v-if="foldersState.welcome" class="folder-content">
              <div 
                v-for="block in filteredPrimaryBlocks" 
                :key="block._id" 
                class="flow-button"
                :class="{ 'active': selectedBlock?._id === block._id }"
                @click="handleBlockClick(block)"
              >
                {{ block.displayName }}
              </div>
            </div>
          </div>

          <!-- Standard Flows Section -->
          <div class="folder-section">
            <div class="folder-header" @click="toggleFolder('standard')">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" :class="{ 'rotated': !foldersState.standard }">
                <polyline points="6 9 12 15 18 9"></polyline>
              </svg>
              <span>Flux Standards</span>
            </div>
            <div v-if="foldersState.standard" class="folder-content">
              <div 
                v-for="block in filteredSecondaryBlocks" 
                :key="block.idblock" 
                class="flow-button"
                :class="{ 'active': selectedBlock && selectedBlock.idblock === block.idblock }"
                @click="handleBlockClick(block)"
              >
                {{ block.displayName }}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Main Content -->
    <div class="main-content">
      <div class="workspace">
        
        <!-- Flow builder canvas -->
          <div v-if="!selectedBlock" class="empty-state">
            <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1">
              <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"></path>
            </svg>Contenu du flux













            <p>Sélectionnez un flux dans la barre latérale pour commencer l'édition</p>
          </div>

          <div v-else>
            
            <div class="block-header">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"></path>
              </svg>
              <div class="block-name-container">
                <input
                  v-if="isEditingBlockName"
                  v-model="editedBlockName"
                  @blur="saveBlockName"
                  @keyup.enter="saveBlockName"
                  @keyup.esc="cancelBlockNameEdit"
                  ref="blockNameInput"
                  class="block-name-input"
                  :placeholder="selectedBlock.blockname ? 'Modifier le nom' : 'Créer un nom pour le bloc'"
                  maxlength="20"
                  @input="validateBlockName"
                />
                <span
                  v-if="isEditingBlockName"
                  class="char-count"
                  :class="{ 'char-count-limit': editedBlockName.length >= 20 }"
                >
                  {{ editedBlockName.length }}/20
                </span>
                <div v-else class="block-name-wrapper">
                  <span
                    @click="startEditingBlockName"
                    class="block-name"
                    :class="{ 'no-name': !selectedBlock.blockname }"
                  >
                    {{ selectedBlock.blockname || 'Cliquez pour ajouter un nom' }}
                  </span>
                  <div class="block-id-container">
                    <span class="block-id">{{ selectedBlock.idblock }}</span>
                    <button 
                      class="copy-id-btn" 
                      @click="copyIdBlock" 
                      :title="`Copier l'ID: ${selectedBlock.idblock}`"
                    >
                      <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                        <rect x="9" y="9" width="13" height="13" rx="2" ry="2"></rect>
                        <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"></path>
                      </svg>
                    </button>
                  </div>
                </div>
              </div>
              <!-- Jarvis Toggle Switch -->
              <div v-if="selectedBlock.type === 'default_block'" class="jarvis-toggle" v-bind="jarvisToggleDataAttr">
                <span class="jarvis-label">
                  <svg class="brain-icon" width="30" height="30" viewBox="0 0 300 300" fill="currentColor">
                    <g transform="translate(0.000000,300.000000) scale(0.100000,-0.100000)">
                      <path d="M1150 2939 c-103 -20 -236 -118 -270 -198 -9 -23 -17 -30 -29 -25 -9 3 -46 9 -82 11 -152 12 -302 -74 -373 -215 -35 -70 -52 -167 -39 -231 l10 -51 -41 -19 c-188 -84 -280 -308 -207 -501 l20 -51 -39 -40 c-63 -65 -84 -120 -88 -226 -3 -66 1 -106 12 -140 23 -70 83 -143 148 -181 l56 -33 -24 -42 c-51 -92 -49 -236 3 -344 49 -99 171 -194 269 -209 41 -6 41 -6 47 -55 8 -65 60 -161 111 -206 97 -84 236 -114 351 -75 59 20 60 20 86 1 79 -56 219 -69 320 -30 71 27 141 88 179 155 l30 51 0 198 0 197 90 0 89 0 24 -35 24 -35 -23 -47 c-30 -58 -30 -98 -1 -154 62 -116 219 -121 295 -9 33 49 28 142 -11 193 -32 42 -102 72 -150 64 -30 -5 -35 -2 -53 31 -44 83 -43 82 -170 82 l-114 0 0 108 0 107 287 -2 288 -2 72 -83 73 -83 0 -77 0 -77 -42 -23 c-120 -66 -115 -244 8 -303 52 -25 132 -17 177 18 98 75 86 231 -22 286 l-37 19 4 94 4 94 -92 110 c-51 60 -98 112 -104 116 -6 4 -147 8 -313 8 l-303 0 0 85 0 85 308 0 307 -1 124 -144 125 -145 102 0 c99 0 102 -1 108 -24 8 -33 68 -83 116 -96 72 -19 152 17 187 83 22 44 21 127 -4 169 -30 52 -77 78 -140 78 -30 0 -67 -7 -83 -15 -30 -16 -80 -74 -80 -93 0 -8 -27 -12 -82 -12 l-83 0 -97 113 c-53 61 -110 127 -127 145 l-30 32 -325 0 -326 0 0 160 0 160 326 0 326 0 35 38 c19 20 76 85 126 144 l92 108 81 0 c78 0 82 -1 88 -24 8 -33 68 -83 116 -97 54 -16 113 2 161 50 115 116 -3 323 -161 281 -48 -13 -108 -63 -116 -96 -6 -23 -9 -24 -105 -24 -114 0 -89 17 -257 -177 l-97 -113 -307 0 -308 0 0 85 0 85 303 0 c166 0 307 4 313 8 6 4 53 56 103 116 l92 109 -2 94 -2 95 39 22 c131 73 101 268 -48 307 -42 12 -53 11 -94 -7 -136 -56 -151 -232 -26 -302 l42 -23 0 -79 0 -78 -72 -86 -73 -85 -287 -1 -288 0 0 110 0 109 118 3 117 3 34 53 c34 52 34 52 86 52 60 0 106 25 143 80 33 49 28 142 -11 193 -34 44 -101 72 -157 64 -117 -15 -190 -173 -120 -259 17 -22 17 -24 -4 -60 l-21 -38 -92 0 -93 0 0 188 0 187 -29 50 c-56 99 -162 173 -276 194 -66 12 -82 12 -145 0z"/>
                      <path d="M730 1858 c-51 -26 -62 -54 -30 -83 19 -18 22 -18 56 -2 42 21 116 22 152 3 15 -8 40 -28 56 -45 47 -49 106 -34 92 23 -8 33 -86 100 -134 114 -64 19 -142 15 -192 -10z"/>
                      <path d="M950 1285 c-8 -10 -10 -29 -6 -56 18 -115 113 -204 230 -216 62 -6 96 8 96 40 0 33 -19 47 -64 47 -90 1 -159 58 -172 143 -9 52 -56 76 -84 42z"/>
                      <path d="M572 1278 c-7 -7 -12 -37 -12 -67 0 -106 65 -198 165 -237 56 -21 80 -12 80 32 0 24 -7 32 -41 49 -82 39 -114 85 -114 163 0 52 -12 72 -45 72 -12 0 -26 -5 -33 -12z"/>
                    </g>
                  </svg>
                  GRIOT IA
                </span>
                <div class="jarvis-controls">
                  <router-link 
                    :to="`/dashboard/chatbots/${$route.params.idchatbot}/jarvis-knowledge?from=playground`" 
                    class="jarvis-knowledge-btn"
                    title="Gérer les connaissances de Jarvis"
                  >
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                      <path d="M2 3h6a4 4 0 0 1 4 4v14a3 3 0 0 0-3-3H2z"></path>
                      <path d="M22 3h-6a4 4 0 0 0-4 4v14a3 3 0 0 1 3-3h7z"></path>
                    </svg>
                  </router-link>
                  <div class="checkbox-wrapper-9">
                    <input 
                      class="tgl tgl-flat" 
                      :id="'jarvis-toggle-' + selectedBlock._id" 
                      type="checkbox"
                      v-model="selectedBlock.jarvis"
                      @change="updateJarvisState"
                    >
                    <label class="tgl-btn" :for="'jarvis-toggle-' + selectedBlock._id"></label>
                  </div>
                </div>
              </div>
            </div>
            
 
            <!-- Links Card -->
            <div class="links-card" v-if="selectedBlock">
              <div class="links-header" @click="toggleLinksExpanded">
                <div class="links-header-content">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                    <path d="M9 17H7A5 5 0 0 1 7 7h2"/>
                    <path d="M15 7h2a5 5 0 1 1 0 10h-2"/>
                    <line x1="10" y1="12" x2="14" y2="12"/>
                  </svg>
                  <span class="links-title-text">Liens</span>
                  <div class="links-summary">
                    {{ totalLinksCount }}
                  </div>
                </div>
                <svg 
                  class="collapse-icon" 
                  :class="{ 'expanded': isLinksExpanded }"
                  width="16" 
                  height="16" 
                  viewBox="0 0 24 24" 
                  fill="none" 
                  stroke="currentColor" 
                  stroke-width="2"
                >
                  <polyline points="6 9 12 15 18 9"></polyline>
                </svg>
              </div>

              <div class="links-content" v-show="isLinksExpanded">
                <!-- Inbound Links -->
                <div class="links-group" v-if="inboundLinks.length > 0 || loadingInboundLinks">
                  <div class="group-header">
                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                      <path d="M19 12H5M12 19l-7-7 7-7"/>
                    </svg>
                    <span>Entrants ({{ inboundLinks.length }})</span>
                  </div>
                  
                  <div v-if="loadingInboundLinks" class="loading-state">
                    <div class="loading-spinner"></div>
                    <span>Chargement...</span>
                  </div>
                  
                  <div v-else-if="inboundLinks.length === 0" class="empty-state">
                    Aucun lien entrant
                  </div>
                  
                  <div v-else class="links-grid">
                    <div 
                      v-for="link in inboundLinks" 
                      :key="`in-${link.idblock}`"
                      class="link-badge inbound"
                      @click="navigateToBlock(link.idblock)"
                      :title="`${link.blockname || link.idblock} - ${link.sourceType || 'Bloc Standard'}`"
                    >
                      <span class="badge-text">{{ link.blockname || link.idblock }}</span>
                      <span class="badge-source" v-if="link.sourceType">{{ link.sourceType }}</span>
                    </div>
                  </div>
                </div>

                <!-- Outbound Links -->
                <div class="links-group" v-if="outboundLinks.length > 0">
                  <div class="group-header">
                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                      <path d="M5 12h14M12 5l7 7-7 7"/>
                    </svg>
                    <span>Sortants ({{ outboundLinks.length }})</span>
                  </div>
                  
                  <div class="links-grid">
                    <div 
                      v-for="link in outboundLinks" 
                      :key="`out-${link.id}`"
                      class="link-badge outbound"
                      @click="navigateToBlock(link.targetBlock)"
                      :title="`${link.targetName || link.targetBlock} - ${link.sourceType}`"
                    >
                      <span class="badge-text">{{ link.targetName || link.targetBlock }}</span>
                      <span class="badge-source">{{ link.sourceType }}</span>
                    </div>
                  </div>
                </div>

                <!-- Empty state when no links -->
                <div v-if="!loadingInboundLinks && inboundLinks.length === 0 && outboundLinks.length === 0" class="no-links-state">
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                    <circle cx="12" cy="12" r="3"/>
                    <path d="M12 1v6M12 17v6M4.22 4.22l4.24 4.24M15.54 15.54l4.24 4.24M1 12h6M17 12h6M4.22 19.78l4.24-4.24M15.54 8.46l4.24-4.24"/>
                  </svg>
                  <span>Aucun lien de navigation détecté</span>
                </div>
              </div>
            </div>
          </div>
            <!-- Flow elements displayed using dynamic components -->
          <div 
            v-for="(element, index) in selectedBlock?.flow || []" 
            :key="element.id_element || index" 
            class="flow-element-wrapper" 
            :class="'element-type-' + element.type"
            draggable="true"
            @dragstart="dragStart($event, index)"
            @dragover.prevent="dragOver($event, index)"
            @drop="drop($event, index)"
          >
        
            
            <!-- Utilisation du composant dynamique approprié -->
            <component
              :is="getElementComponent(element.type)"
              :element="element"
              :index="index"
              :id-bot="$route.params.idchatbot"
              :variables="variables"
              @update="updateElement($event, index)"
              @remove="deleteElement(index)"
              @duplicate="duplicateElement(index)"
              @notification="showNotification"
              class="dynamic-element"
            >
              <!-- Fallback en cas de composant non trouvé -->
             
            </component>
            
            <!-- Footer avec informations de l'élément -->
             
          </div>

        <!-- Tools sidebar -->
        <div class="tools-sidebar">
          
          <!-- Text Element -->
          <button class="tool-btn" @click="insertElement('text')" @mouseenter="showElementInfo('text', $event)" @mouseleave="hideElementInfo" title="Texte - Message simple">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z"/>
              <path d="M14 2v6h6"/>
              <path d="M16 13H8"/>
              <path d="M16 17H8"/>
              <path d="M10 9H8"/>
            </svg>
          </button>
          
          <!-- Text with Buttons Element -->
          <button class="tool-btn" @click="insertElement('text_button')" @mouseenter="showElementInfo('text_button', $event)" @mouseleave="hideElementInfo" title="Texte avec boutons - Message avec boutons interactifs">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <rect x="3" y="3" width="18" height="18" rx="2" ry="2"/>
              <path d="M9 9h6"/>
              <path d="M9 15h6"/>
              <rect x="7" y="12" width="2" height="2"/>
              <rect x="7" y="16" width="2" height="2"/>
            </svg>
          </button>
          
          <!-- List Element -->
          <button class="tool-btn" @click="insertElement('list')" @mouseenter="showElementInfo('list', $event)" @mouseleave="hideElementInfo" title="Liste interactive - Menu de sélection">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <line x1="8" y1="6" x2="21" y2="6"/>
              <line x1="8" y1="12" x2="21" y2="12"/>
              <line x1="8" y1="18" x2="21" y2="18"/>
              <line x1="3" y1="6" x2="3.01" y2="6"/>
              <line x1="3" y1="12" x2="3.01" y2="12"/>
              <line x1="3" y1="18" x2="3.01" y2="18"/>
            </svg>
          </button>
          
          <!-- Image Element -->
          <button class="tool-btn" @click="insertElement('image')" @mouseenter="showElementInfo('image', $event)" @mouseleave="hideElementInfo" title="Image - Contenu visuel">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <rect x="3" y="3" width="18" height="18" rx="2" ry="2"/>
              <circle cx="8.5" cy="8.5" r="1.5"/>
              <path d="M21 15l-5-5L5 21"/>
            </svg>
          </button>
          
          <!-- Document Element -->
          <button class="tool-btn" @click="insertElement('document')" @mouseenter="showElementInfo('document', $event)" @mouseleave="hideElementInfo" title="Document - Fichier PDF">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z"/>
              <path d="M14 2v6h6"/>
              <path d="M16 13H8"/>
              <path d="M16 17H8"/>
              <path d="M10 9H8"/>
            </svg>
          </button>
          
          <!-- Audio Element -->
          <button class="tool-btn" @click="insertElement('audio')" @mouseenter="showElementInfo('audio', $event)" @mouseleave="hideElementInfo" title="Audio - Fichier audio">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <path d="M9 18V5l12-2v13"/>
              <circle cx="6" cy="18" r="3"/>
              <circle cx="18" cy="16" r="3"/>
            </svg>
          </button>
          
          <!-- Delay Element -->
          <button class="tool-btn" @click="insertElement('delay')" @mouseenter="showElementInfo('delay', $event)" @mouseleave="hideElementInfo" title="Délai - Pause dans le flux">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <circle cx="12" cy="12" r="10"/>
              <path d="M12 6v6l4 2"/>
            </svg>
          </button>
          
          <!-- Request Element -->
          <button class="tool-btn" @click="insertElement('request')" @mouseenter="showElementInfo('request', $event)" @mouseleave="hideElementInfo" title="Requête HTTP - Appel API">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <path d="M21 12c0 1.66-4 3-9 3s-9-1.34-9-3"/>
              <path d="M3 5v14c0 1.66 4 3 9 3s9-1.34 9-3V5"/>
              <ellipse cx="12" cy="5" rx="9" ry="3"/>
            </svg>
          </button>
          
          <!-- Answer Element -->
          <button class="tool-btn" @click="insertElement('answer')" @mouseenter="showElementInfo('answer', $event)" @mouseleave="hideElementInfo" title="Réponse utilisateur - Capture d'input">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <path d="M21 15a2 2 0 01-2 2H7l-4 4V5a2 2 0 012-2h14a2 2 0 012 2z"/>
              <path d="M13 8H7"/>
              <path d="M17 12H7"/>
            </svg>
          </button>
          
          <!-- Redirect Element -->
          <button class="tool-btn" @click="insertElement('redirect')" @mouseenter="showElementInfo('redirect', $event)" @mouseleave="hideElementInfo" title="Redirection - Changement de flux">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <path d="M5 12h14"/>
              <path d="M12 5l7 7-7 7"/>
            </svg>
          </button>
          
          <!-- Notification Element -->
          <button class="tool-btn" @click="insertElement('notification')" @mouseenter="showElementInfo('notification', $event)" @mouseleave="hideElementInfo" title="Notification - Alerte">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <path d="M18 8A6 6 0 006 8c0 7-3 9-3 9h18s-3-2-3-9"/>
              <path d="M13.73 21a2 2 0 01-3.46 0"/>
            </svg>
          </button>
          
          <!-- Variable Insert Element -->
          <button class="tool-btn" @click="insertElement('variable_insert')" @mouseenter="showElementInfo('variable_insert', $event)" @mouseleave="hideElementInfo" title="Insertion de variable - Gestion de variables">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <path d="M7 21h10"/>
              <path d="M12 21v-8"/>
              <path d="M5 11c6-6 6-6 12 0"/>
              <circle cx="12" cy="7" r="4"/>
            </svg>
          </button>
          
          <!-- Location Request Element -->
          <button class="tool-btn" @click="insertElement('location_request_message')" @mouseenter="showElementInfo('location_request_message', $event)" @mouseleave="hideElementInfo" title="Demande de localisation - Demander la position de l'utilisateur">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"/>
              <circle cx="12" cy="10" r="3"/>
            </svg>
          </button>
          
          <!-- Button Link Element -->
          <button class="tool-btn" @click="insertElement('button_link')" @mouseenter="showElementInfo('button_link', $event)" @mouseleave="hideElementInfo" title="Carte avec bouton - Image, texte et bouton d'action">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <rect x="3" y="3" width="18" height="14" rx="2" ry="2"/>
              <path d="M7 17v-2a2 2 0 0 1 2-2h6a2 2 0 0 1 2 2v2"/>
              <circle cx="12" cy="9" r="2"/>
            </svg>
          </button>
        </div>

        <!-- Panneau d'information dynamique -->
        <div v-if="elementInfo.show" class="element-info-panel">
          <h4>{{ elementInfo.title }}</h4>
          <p>{{ elementInfo.description }}</p>
        </div>

        <!-- Test Elements button -->
        
      </div>
    </div>
  </div>
  
  <!-- Modal pour créer un nouveau bloc -->
  <div class="modal" v-if="showCreateBlockModal">
    <div class="modal-backdrop" @click="closeCreateBlockModal"></div>
    <div class="modal-content">
      <div class="modal-header">
        <h3>Créer un nouveau flux</h3>
        <button class="close-btn" @click="closeCreateBlockModal">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <line x1="18" y1="6" x2="6" y2="18"></line>
            <line x1="6" y1="6" x2="18" y2="18"></line>
          </svg>
        </button>
      </div>
      <div class="modal-body">
        <div class="form-group">
          <label for="blockName">Nom du flux</label>
          <input 
            type="text" 
            id="blockName" 
            v-model="newBlock.name" 
            @input="generateIdBlock"
            placeholder="Entrez le nom du flux"
            maxlength="20"
          />
          <small class="char-count">{{ newBlock.name.length }}/20 caractères</small>
        </div>
        <div class="form-group">
          <label for="blockId">ID du flux</label>
          <input 
            type="text" 
            id="blockId" 
            v-model="newBlock.idblock" 
            disabled
            placeholder="Généré automatiquement"
          />
        </div>
      </div>
      <div class="modal-footer">
        <button class="cancel-btn" @click="closeCreateBlockModal">Annuler</button>
        <button 
          class="create-btn" 
          @click="createNewBlock" 
          :disabled="!newBlock.name || creatingBlock"
        >
          <span v-if="!creatingBlock">Créer</span>
          <span v-else>Création en cours...</span>
        </button>
      </div>
    </div>
  </div>
  
  <!-- Composant de notification -->
  <div class="notification-wrapper" v-if="notification.show">
    <div class="notification" :class="notification.type">
      <svg v-if="notification.type === 'success' || notification.type === 'saved'" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
        <path d="M20 6L9 17l-5-5"></path>
      </svg>
      <svg v-else-if="notification.type === 'error'" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
        <circle cx="12" cy="12" r="10"></circle>
        <line x1="15" y1="9" x2="9" y2="15"></line>
        <line x1="9" y1="9" x2="15" y2="15"></line>
      </svg>
      <svg v-else-if="notification.type === 'saving'" class="spinning" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
        <circle cx="12" cy="12" r="10"/>
      </svg>
      <span>{{ notification.message }}</span>
    </div>
  </div>
</template>

<script>
import { axiosPeelo } from '@/utils/axios';
import { ELEMENT_COMPONENTS } from '@/components/flowelements';
import axios from 'axios';

export default {
  name: 'PlaygroundView',
  
  components: {
  },
  
  data() {
    return {
      chatbot: {},
      blocks: [],
      primaryBlocks: [],
      secondaryBlocks: [],
      filteredPrimaryBlocks: [],
      filteredSecondaryBlocks: [],
      searchQuery: '',
      loading: true,
      error: null,
      isSidebarExpanded: true,
      foldersState: {
        welcome: true,
        standard: true
      },
      scrollPosition: 0,
      selectedBlock: null,
      showCreateBlockModal: false,
      newBlock: {
        name: '',
        idblock: ''
      },
      creatingBlock: false,
      notification: {
        show: false,
        message: '',
        type: 'success',
        timeout: null
      },
      variables: [], // Ajout de la propriété variables
      ELEMENT_COMPONENTS, // Exposition de ELEMENT_COMPONENTS pour le template
      element_template: {
        text: {
          type: "text",
          id_element: null,
          id_previous: null,
          message: "",
          preview_url: true
        },
        text_button: {
          type: "text_button",
          id_element: null,
          id_previous: null,
          message: "Ecris ton texte ici",
          buttons: [
            {
              id: "idbutton1",
              title: "Placeholder"
            },
            {
              id: "idbutton2",
              title: "Placeholder"
             } 
          ]
        },
        list: {
          type: "list",
          id_element: null,
          id_previous: null,
          interactive: {
            type: "list",
            header: {
              type: "text",
              text: "HEADER_TEXT"
            },
            body: {
              text: "BODY_TEXT"
            },
            footer: {
              text: "FOOTER_TEXT"
            },
            action: {
              button: "BUTTON_TEXT",
              sections: [
                {
                  title: "SECTION_1_TITLE",
                  rows: [
                    {
                      id: "SECTION_1_ROW_1_ID",
                      title: "SECTION_1_ROW_1_TITLE",
                      description: "SECTION_1_ROW_1_DESCRIPTION"
                    },
                    {
                      id: "SECTION_1_ROW_2_ID",
                      title: "SECTION_1_ROW_2_TITLE",
                      description: "SECTION_1_ROW_2_DESCRIPTION"
                    }
                  ]
                },
                {
                  title: "SECTION_2_TITLE",
                  rows: [
                    {
                      id: "SECTION_2_ROW_1_ID",
                      title: "SECTION_2_ROW_1_TITLE",
                      description: "SECTION_2_ROW_1_DESCRIPTION"
                    },
                    {
                      id: "SECTION_2_ROW_2_ID",
                      title: "SECTION_2_ROW_2_TITLE",
                      description: "SECTION_2_ROW_2_DESCRIPTION"
                    }
                  ]
                }
              ]
            }
          }
        },
        image: {
          type: "image",
          id_element: null,
          id_previous: null,
          link: ""
        },
        document: {
          type: "document",
          id_element: null,
          id_previous: null,
          link: ""
        },
        audio: {
          type: "audio",
          id_element: null,
          id_previous: null,
          link: ""
        },
        delay: {
          type: "delay",
          id_element: null,
          id_previous: null,
          timmer: ""
        },
        request: {
          type: "request",
          id_element: null,
          id_previous: null,
          method: "POST",
          url: "https://api.peelo.chat/send/datas",
          datas: ["variablename1", "variablename2", "variablename3"]
        },
        answer: {
          type: "answer",
          id_element: null,
          id_previous: null,
          options: {
            answer_type: ["interactive (ou bien text ) "],
            variable: {
              id: "departureter"
            },
            default_reply: "Désolé je ne comprends pas, ..."
          }
        },
        redirect: {
          type: "redirection",
          id_element: null,
          id_previous: null,
          redirection_block: "block name"
        },
        notification: {
          type:'notification',
          to: "2217XXXXXXXX",
          id_element: null,
          id_previous: null,
          notification: {
            type: "text",
            message: "",
            preview_url: true
          }
        },
        variable_insert: {
          "type": "variable_insert",
          "id_element": null,
          "id_previous": null,
          "variable": {
            "id": "idvariable",
            "value": "user"
          }
        },
        location_request_message: {
          type: "location_request_message",
          id_element: null,
          id_previous: null,
          message: "Vous devez cliquer sur le bouton ci-dessous pour envoyer votre localisation"
        },
        button_link: {
          type: "button_link",
          id_element: null,
          id_previous: null,
          image_header: "",
          body_message: "Tap the button below to see available dates.",
          action: {
            name: "cta_url",
            parameters: {
              display_text: "See Dates",
              url: "https://example.com"
            }
          },
          footer_message: "Dates subject to change."
        }
      },
      elementInfo: {
        show: false,
        title: '',
        description: ''
      },
      isEditingBlockName: false,
      editedBlockName: '',
      saveTimeout: null,
      inboundLinks: [],
      outboundLinks: [],
      loadingInboundLinks: false,
      isLinksExpanded: false,
      totalLinksCount: 0,
      showFilter: false,
      filterQuery: '',
      filteredInboundLinks: [],
      filteredOutboundLinks: [],
      isOnline: navigator.onLine,
    };
  },

  async created() {
    await this.initializeData();
    
    // Vérifier si un idblock est présent dans l'URL et le sélectionner
    if (this.$route.params.idblock) {
      await this.selectBlockFromRoute();
    }
  },

  mounted() {
    // Ajouter les event listeners pour surveiller la connexion
    window.addEventListener('online', this.handleOnline);
    window.addEventListener('offline', this.handleOffline);
  },

  beforeUnmount() {
    // Nettoyer les event listeners
    window.removeEventListener('online', this.handleOnline);
    window.removeEventListener('offline', this.handleOffline);
    
    // Nettoyer le timeout de notification
    if (this.notification.timeout) {
      clearTimeout(this.notification.timeout);
    }
  },

  watch: {
    // Watcher optimisé pour détecter les changements de route
    '$route': {
      handler(to, from) {
        if (to.params.idblock !== from.params.idblock) {
          if (to.params.idblock) {
            this.selectBlockFromRoute();
          } else {
            this.selectedBlock = null;
          }
        }
      },
      immediate: false
    }
  },

  methods: {
    // Méthodes pour les tooltips - copié de Sidebar.vue
    showTooltip(event) {
      const tooltip = event.currentTarget.querySelector('.tooltip-text');
      if (tooltip) {
        const rect = event.currentTarget.getBoundingClientRect();
        
        // Position fixe pour être toujours au-dessus de tous les éléments
        tooltip.style.position = 'fixed';
        
        // Vérifier si c'est un bouton de la tools-sidebar
        const isToolsSidebar = event.currentTarget.closest('.tools-sidebar');
        
        if (isToolsSidebar) {
          // Position à gauche de la tools-sidebar
          tooltip.style.left = `${rect.left - tooltip.offsetWidth - 10}px`;
        } else {
          // Position normale à droite de l'élément
          tooltip.style.left = `${rect.right + 10}px`;
        }
        
        // La position verticale reste la même
        tooltip.style.top = `${rect.top + (rect.height / 2)}px`;
        
        // Ajouter la classe visible
        tooltip.classList.add('visible');
      }
    },

    hideTooltip(event) {
      const tooltip = event.currentTarget.querySelector('.tooltip-text');
      if (tooltip) {
        tooltip.classList.remove('visible');
      }
    },

    async initializeData() {
      this.loading = true;
      this.error = null;
      
      try {
        await this.getChatbotInfo();
        await this.fetchVariables();
      } catch (error) {
        console.error('Error initializing data:', error);
        this.error = 'Une erreur est survenue lors du chargement des données';
      } finally {
        this.loading = false;
      }
    },

    async getChatbotInfo() {
      try {
        // Récupérer le token depuis l'objet user dans localStorage
        const userData = localStorage.getItem('user');
        if (!userData) {
          throw new Error('Données utilisateur non trouvées');
        }
        
        const userObj = JSON.parse(userData);
        const token = userObj.data.token;
        
        // Configurer les headers avec le token
        const config = {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        };
        
        // Faire la requête avec le token
        const response = await axiosPeelo.get(
          `/strong/get/chatbot/${this.$route.params.idchatbot}`,
          config
        );
        
        if (response.data && response.data.success) {
          this.chatbot = response.data.chatbot;
          window.sessionStorage.setItem('Chatbot', JSON.stringify(this.chatbot));
          await this.getBlocks();
        } else {
          throw new Error(response.data.error || 'Erreur lors de la récupération du chatbot');
        }
      } catch (error) {
        console.error('Error fetching chatbot info:', error);
        this.showNotification({
          message: `Erreur: ${error.message || 'Impossible de récupérer les informations du chatbot'}`,
          type: "error"
        });
        throw error;
      }
    },

    async getBlocks() {
      try {
        // Récupérer le token depuis l'objet user dans localStorage
        const userData = localStorage.getItem('user');
        if (!userData) {
          throw new Error('Données utilisateur non trouvées');
        }
        
        const userObj = JSON.parse(userData);
        const token = userObj.data.token;
        
        // Configurer les headers avec le token
        const config = {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        };
        
        const response = await axiosPeelo.get(
          `/strong/get/chatbot/${this.$route.params.idchatbot}/flow`,
          config
        );
        
        if (response.data && response.data.success) {
          const flowData = response.data.flow;
          
          // Traiter les blocs principaux
          this.primaryBlocks = [];
          if (flowData.mainblocks) {
            // Welcome blocks
            if (flowData.mainblocks.welcome) {
              this.primaryBlocks.push(...flowData.mainblocks.welcome.map(block => ({
                ...block,
                category: 'welcome',
                displayName: 'Welcome Flow'
              })));
            }
            
            // Default blocks
            if (flowData.mainblocks.default) {
              this.primaryBlocks.push(...flowData.mainblocks.default.map(block => ({
                ...block,
                category: 'default',
                displayName: 'Default Flow'
              })));
            }
            
            // Commerce blocks
            if (flowData.mainblocks.commerce) {
              this.primaryBlocks.push(...flowData.mainblocks.commerce.map(block => ({
                ...block,
                category: 'commerce',
                displayName: 'Commerce Flow'
              })));
            }
          }
          
          // Traiter les blocs secondaires
          this.secondaryBlocks = flowData.blocks ? flowData.blocks.map(block => ({
            ...block,
            category: 'standard',
            displayName: block.blockname && block.blockname !== null ? block.blockname : block.idblock
          })) : [];
          
          // Combiner tous les blocs
          this.blocks = [...this.primaryBlocks, ...this.secondaryBlocks];
          
          // Initialiser les blocs filtrés avec tous les blocs
          this.filteredPrimaryBlocks = [...this.primaryBlocks];
          this.filteredSecondaryBlocks = [...this.secondaryBlocks];
        } else {
          this.error = response.data.error || 'Erreur lors de la récupération des flows';
        }
      } catch (error) {
        this.error = 'Erreur lors de la récupération des flows';
        console.error('Error fetching blocks:', error);
      }
    },

    // Méthode pour filtrer les blocs en fonction de la recherche
    filterBlocks() {
      const query = this.searchQuery.toLowerCase().trim();
      
      if (!query) {
        // Si la recherche est vide, afficher tous les blocs
        this.filteredPrimaryBlocks = [...this.primaryBlocks];
        this.filteredSecondaryBlocks = [...this.secondaryBlocks];
        return;
      }
      
      // Filtrer les blocs principaux
      this.filteredPrimaryBlocks = this.primaryBlocks.filter(block => {
        return (
          block.name?.toLowerCase().includes(query) ||
          block.idblock?.toLowerCase().includes(query) ||
          block.displayName?.toLowerCase().includes(query)
        );
      });
      
      // Filtrer les blocs secondaires
      this.filteredSecondaryBlocks = this.secondaryBlocks.filter(block => {
        return (
          block.name?.toLowerCase().includes(query) ||
          block.idblock?.toLowerCase().includes(query) ||
          block.displayName?.toLowerCase().includes(query)
        );
      });
    },

    toggleFolder(folderName) {
      this.foldersState[folderName] = !this.foldersState[folderName];
    },

    async handleBlockClick(block) {
      // Si c'est le bloc déjà sélectionné, déselectionner en naviguant sans idblock
      if (this.selectedBlock && this.selectedBlock.idblock === block.idblock) {
        this.$router.push(`/dashboard/chatbots/${this.$route.params.idchatbot}/playground`);
      } else {
        // Naviguer vers l'URL avec l'idblock
        this.$router.push(`/dashboard/chatbots/${this.$route.params.idchatbot}/playground/${block.idblock}`);
      }
    },

    async selectBlockFromRoute() {
      const idblock = this.$route.params.idblock;
      if (!idblock) {
        this.selectedBlock = null;
        return;
      }

      // Chercher le bloc dans les listes existantes
      let targetBlock = this.blocks.find(block => block.idblock === idblock);
      
      if (!targetBlock) {
        // Si le bloc n'est pas trouvé dans les listes actuelles, attendre que les données soient chargées
        let retries = 0;
        while (!targetBlock && retries < 5) {
          await new Promise(resolve => setTimeout(resolve, 100));
          targetBlock = this.blocks.find(block => block.idblock === idblock);
          retries++;
        }
      }

      if (!targetBlock) {
        this.showNotification({
          message: "Bloc non trouvé",
          type: "error"
        });
        // Rediriger vers la page playground sans idblock
        this.$router.replace(`/dashboard/chatbots/${this.$route.params.idchatbot}/playground`);
        return;
      }

      // Mettre à jour le bloc sélectionné immédiatement pour l'UI
      this.selectedBlock = targetBlock;
      
      // Récupérer les détails complets du bloc depuis l'API
      try {
        const userData = localStorage.getItem('user');
        if (!userData) throw new Error('Données utilisateur non trouvées');
        
        const userObj = JSON.parse(userData);
        const token = userObj.data.token;
        const config = { headers: { 'Authorization': `Bearer ${token}` } };
        
        const response = await axiosPeelo.get(
          `/strong/get/flow/${this.$route.params.idchatbot}/${targetBlock._id}`, 
          config
        );
        
        if (response.data && response.data.success) {
          // Traiter la structure de réponse de l'API
          if (response.data.block && Array.isArray(response.data.block) && response.data.block.length > 0) {
            // Récupérer le premier élément du tableau block
            const blockData = response.data.block[0];
            
            // Conserver les propriétés displayName et category du bloc original
            blockData.displayName = targetBlock.displayName;
            blockData.category = targetBlock.category;
            
            // Mettre à jour le bloc sélectionné avec les données complètes
            this.selectedBlock = blockData;

            
            // Charger les liens après avoir récupéré les détails du bloc
            await this.loadInboundLinks();
            this.analyzeOutboundLinks();
            this.updateTotalLinksCount();
          } else {
            throw new Error('Structure de données inattendue dans la réponse');
          }
        } else {
          throw new Error(response.data.error || 'Erreur lors de la récupération des détails du bloc');
        }
      } catch (error) {
        console.error('Erreur lors de la récupération des détails du bloc:', error);
        this.showNotification({
          message: `Erreur: ${error.message || 'Impossible de récupérer les détails du bloc'}`,
          type: "error"
        });
      }
    },

    goBack() {
      this.$router.push(`/dashboard/chatbots/${this.$route.params.idchatbot}/home`);
    },
    
    // Méthode pour afficher les notifications
    showNotification({ message, type = 'success', duration = 5000 }) {

      if (this.notification.timeout) {
        clearTimeout(this.notification.timeout);
      }
      this.notification = {
        show: true,
        message,
        type,
        timeout: setTimeout(() => {
          this.notification.show = false;
        }, 3000)
      };
    },
    
    // Méthodes pour la création de bloc
    openCreateBlockModal() {
      this.showCreateBlockModal = true;
      this.newBlock = {
        name: '',
        idblock: ''
      };
    },
    
    closeCreateBlockModal() {
      this.showCreateBlockModal = false;
    },
    
    generateIdBlock() {
      if (this.newBlock.name) {
        // Convertir en minuscules et nettoyer tous les caractères spéciaux
        let baseId = this.newBlock.name
        .toLowerCase()
          .normalize('NFD') // Décomposer les caractères accentués
          .replace(/[\u0300-\u036f]/g, '') // Supprimer les diacritiques
          .replace(/[^a-z0-9\s]/g, '') // Garder seulement lettres, chiffres et espaces
          .replace(/\s+/g, '-') // Remplacer les espaces par des tirets
          .replace(/-+/g, '-') // Remplacer les tirets multiples par un seul tiret
          .replace(/^-+|-+$/g, '');
        // Limiter la longueur de baseId pour que l'ID final ne dépasse pas 20 caractères
        // Réserver 7 caractères pour le tiret et la chaîne aléatoire (6 chars + 1 tiret)
        if (baseId.length > 13) {
          baseId = baseId.substring(0, 13);
        }
        
        // Générer une chaîne aléatoire alphanumérique de 6 caractères
        const randomChars = 'abcdefghijklmnopqrstuvwxyz0123456789';
        let randomString = '';
        for (let i = 0; i < 6; i++) {
          randomString += randomChars.charAt(Math.floor(Math.random() * randomChars.length));
        }
        
        // Combiner pour créer l'ID final
        this.newBlock.idblock = `${baseId}-${randomString}`;
        
        // Vérifier que l'ID final ne dépasse pas 20 caractères
        if (this.newBlock.idblock.length > 20) {
          // Si c'est le cas, réduire encore la partie baseId
          baseId = baseId.substring(0, baseId.length - (this.newBlock.idblock.length - 20));
          this.newBlock.idblock = `${baseId}-${randomString}`;
        }
      } else {
        this.newBlock.idblock = '';
      }
    },
    
    async createNewBlock() {
      if (!this.newBlock.name) return;
      
      this.creatingBlock = true;
      
      try {
        // Récupérer le token
        const userData = localStorage.getItem('user');
        if (!userData) {
          throw new Error('Données utilisateur non trouvées');
        }
        
        const userObj = JSON.parse(userData);
        const token = userObj.data.token;
        
        // Configurer les headers avec le token
        const config = {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        };
        
        // Préparer les données du bloc
        const blockData = {
          name: this.newBlock.name, 
          idblock: this.newBlock.idblock,
          idchatbot: this.$route.params.idchatbot,
          type: 'standard'
        };
        
        // Envoyer la requête
        const response = await axiosPeelo.post(
          `/strong/create/flow/${this.$route.params.idchatbot}`,
          blockData,
          config
        );
        
        // Vérifier si la création a réussi
        if (response.data && response.data.message === "Block créé avec succès") {
          // Afficher une notification de succès
          this.showNotification({
            message: "Le flux a été créé avec succès",
            type: "success"
          });
          
          // Sauvegarder l'ID du nouveau bloc pour le sélectionner après rechargement
          const newBlockId = response.data.block.idblock;
          
          // Fermer le modal et rafraîchir les blocs
          this.closeCreateBlockModal();
          await this.getBlocks();
          
          // Sélectionner automatiquement le nouveau bloc
          const newlyCreatedBlock = this.blocks.find(block => block.idblock === newBlockId);
          if (newlyCreatedBlock) {
            this.selectedBlock = newlyCreatedBlock;
          }
        } else if (response.data && response.data.error === "Un block avec cet ID existe déjà") {
          // Afficher une notification d'erreur pour ID existant
          this.showNotification({
            message: "Un flux avec cet identifiant existe déjà",
            type: "error"
          });
        } else {
          // Gérer d'autres erreurs potentielles
          throw new Error(response.data.error || 'Erreur lors de la création du flux');
        }
      } catch (error) {
        console.error('Error creating block:', error);
        
        // Afficher une notification d'erreur
        this.showNotification({
          message: `Erreur: ${error.message || 'Impossible de créer le flux'}`,
          type: "error"
        });
      } finally {
        this.creatingBlock = false;
      }
    },

    insertElement(elementType) {
      if (!this.selectedBlock) {
        this.showNotification({
          message: "Veuillez d'abord sélectionner un flux pour ajouter des éléments",
          type: "error"
        });
        return;
      }

      // Cloner le template d'élément
      const elementTemplate = JSON.parse(JSON.stringify(this.element_template[elementType]));
      
      // Initialiser le flow s'il n'existe pas
      if (!this.selectedBlock.flow) {
        this.selectedBlock.flow = [];
      }
      
      // Ajouter l'élément au flow
      this.selectedBlock.flow.push(elementTemplate);
      
      // Rafraîchir la chaîne de traçabilité (qui déclenche la sauvegarde)
      this.refreshChain();
      
      // Notification de succès
      this.showNotification({
        message: `Élément "${elementType}" ajouté au flux avec succès`,
        type: "success"
      });
      
     },

    showElementInfo(elementType, event) {
      const elementDescriptions = {
        text: {
          title: 'Texte',
          description: 'Envoie un message texte simple à l\'utilisateur. Peut inclure des liens avec aperçu.'
        },
        text_button: {
          title: 'Texte avec boutons', 
          description: 'Message texte accompagné de boutons cliquables. Permet de créer des menus d\'options.'
        },
        list: {
          title: 'Liste interactive',
          description: 'Affiche une liste déroulante avec sections et options. Idéal pour les menus complexes.'
        },
        image: {
          title: 'Image',
          description: 'Envoie une image à l\'utilisateur. L\'image doit être préalablement uploadée.'
        },
        document: {
          title: 'Document',
          description: 'Envoie un fichier PDF à l\'utilisateur. Le document doit être préalablement uploadé.'
        },
        audio: {
          title: 'Audio',
          description: 'Envoie un fichier audio (message vocal, musique, etc.) à l\'utilisateur.'
        },
        delay: {
          title: 'Délai',
          description: 'Ajoute une pause dans le flux avant de continuer vers l\'élément suivant.'
        },
        request: {
          title: 'Requête HTTP',
          description: 'Effectue un appel API vers un service externe pour récupérer ou envoyer des données.'
        },
        answer: {
          title: 'Réponse utilisateur',
          description: 'Attend une réponse de l\'utilisateur et la stocke dans une variable.'
        },
        redirect: {
          title: 'Redirection',
          description: 'Redirige le flux vers un autre bloc de conversation.'
        },
        notification: {
          title: 'Notification',
          description: 'Envoie une notification à un numéro spécifique.'
        },
        variable_insert: {
          title: 'Insertion de variable',
          description: 'Insère ou modifie une variable dans le contexte de la conversation.'
        },
        location_request_message: {
          title: 'Demande de localisation',
          description: 'Demande à l\'utilisateur de partager sa position géographique via un bouton de localisation.'
        },
        button_link: {
          title: 'Carte avec bouton',
          description: 'Affiche une carte interactive avec image header, texte de corps, texte de pied et bouton d\'action cliquable.'
        }
      };

      const info = elementDescriptions[elementType];
      if (info) {
        this.elementInfo = {
          show: true,
          title: info.title,
          description: info.description
        };
        
        // Positionner le panneau au niveau du bouton
        this.$nextTick(() => {
          const panel = document.querySelector('.element-info-panel');
          if (panel && event) {
            const rect = event.currentTarget.getBoundingClientRect();
            panel.style.top = `${rect.top + (rect.height / 2)}px`;
            panel.style.transform = 'translateY(-50%)';
          }
        });
      }
    },

    hideElementInfo() {
      this.elementInfo = {
        show: false,
        title: '',
        description: ''
      };
    },
    
    // Méthodes pour la gestion des éléments du flux
    getElementTypeLabel(type) {
      const labels = {
        text: 'Texte',
        text_button: 'Texte avec boutons',
        list: 'Liste interactive',
        image: 'Image',
        document: 'Document',
        audio: 'Audio',
        delay: 'Délai',
        request: 'Requête HTTP',
        answer: 'Réponse utilisateur',
        redirect: 'Redirection',
        notification: 'Notification',
        variable_insert: 'Variable'
      };
      return labels[type] || type;
    },
    
    // Gestion du drag and drop pour réorganiser les éléments
    dragStart(event, index) {
      event.dataTransfer.effectAllowed = 'move';
      event.dataTransfer.setData('text/plain', index.toString());
      event.currentTarget.classList.add('dragging');
    },
    
    dragOver(event, index) {
      event.preventDefault();
      const draggingElement = document.querySelector('.dragging');
      if (draggingElement && event.currentTarget !== draggingElement) {
        event.currentTarget.classList.add('drag-over');
      }
    },
    
    drop(event, targetIndex) {
      event.preventDefault();
      const sourceIndex = parseInt(event.dataTransfer.getData('text/plain'));
      document.querySelectorAll('.flow-element').forEach(el => {
        el.classList.remove('dragging');
        el.classList.remove('drag-over');
      });
      
      if (sourceIndex !== targetIndex) {
        this.moveElement(sourceIndex, targetIndex);
      }
    },
    
    moveElement(fromIndex, toIndex) {
      if (!this.selectedBlock || !this.selectedBlock.flow) return;
      
      // Créer une copie du tableau pour la manipulation
      const updatedFlow = [...this.selectedBlock.flow];
      const [movedElement] = updatedFlow.splice(fromIndex, 1);
      updatedFlow.splice(toIndex, 0, movedElement);
      
      // Mettre à jour le flux
      this.selectedBlock.flow = updatedFlow;
      
      // Rafraîchir la chaîne de traçabilité (qui déclenche la sauvegarde)
      this.refreshChain();
      
      // Notification de succès
      this.showNotification({
        message: `Élément déplacé avec succès`,
        type: 'success'
      });
    },
    
    updateElementReferences(flowArray) {
      // Méthode obsolète - remplacée par refreshChain()
      console.warn('updateElementReferences est obsolète, utilisez refreshChain() à la place');
      return flowArray;
    },
    
    editElement(index) {
      // À implémenter: ouvrir un modal d'édition pour l'élément
      this.showNotification({
        message: `Fonctionnalité d'édition à implémenter pour l'élément ${index + 1}`,
        type: 'info'
      });
    },
    
    deleteElement(index) {
      if (confirm('Êtes-vous sûr de vouloir supprimer cet élément ?')) {
        this.selectedBlock.flow.splice(index, 1);
        // Rafraîchir la chaîne de traçabilité (qui déclenche la sauvegarde)
        this.refreshChain();
      }
    },
    
    addFirstElement() {
      if (!this.selectedBlock) return;
      
      // Créer un premier élément texte par défaut
      this.insertElement('text');
    },

    // Nouvelle méthode pour l'édition directe en cliquant sur le message
    editMessageDirectly(index, event) {
      event.stopPropagation(); // Empêcher la propagation de l'événement
      const element = this.selectedBlock.flow[index];
      let currentText = '';
      
      // Détecter le type de message et récupérer le texte actuel
      if (element.type === 'text') {
        currentText = element.text || '';
      } else if (element.type === 'text_button') {
        currentText = element.text || element.message || '';
      }
      
      // Créer un input temporaire pour l'édition
      const tempInput = document.createElement('textarea');
      tempInput.value = currentText;
      tempInput.style.cssText = `
        width: 100%;
        padding: 0.75rem;
        border: 2px solid #667eea;
        border-radius: 12px;
        font-family: inherit;
        font-size: 1rem;
        color: #333;
        background: white;
        resize: vertical;
        min-height: 60px;
        outline: none;
        box-shadow: 0 0 0 3px rgba(102, 126, 234, 0.1);
      `;
      
      // Trouver l'élément de contenu du message
      const messageContent = event.target.closest('.message-content');
      if (messageContent) {
        const originalContent = messageContent.innerHTML;
        messageContent.innerHTML = '';
        messageContent.appendChild(tempInput);
        tempInput.focus();
        tempInput.select();
        
        // Sauvegarder en appuyant sur Entrée ou en perdant le focus
        const saveEdit = () => {
          const newText = tempInput.value.trim();
          if (newText !== '') {
            if (element.type === 'text') {
              element.text = newText;
            } else if (element.type === 'text_button') {
              if ('text' in element) {
                element.text = newText;
              } else {
                element.message = newText;
              }
            }
            this.saveBlock();
            this.showNotification({
              message: 'Message modifié avec succès',
              type: 'success'
            });
          }
          messageContent.innerHTML = originalContent;
        };
        
        // Annuler en appuyant sur Échap
        const cancelEdit = () => {
          messageContent.innerHTML = originalContent;
        };
        
        tempInput.addEventListener('keydown', (e) => {
          if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            saveEdit();
          } else if (e.key === 'Escape') {
            e.preventDefault();
            cancelEdit();
          }
        });
        
        tempInput.addEventListener('blur', saveEdit);
      }
    },

    // Méthode pour sauvegarder le bloc
    async saveBlock() {
      try {
        // Sauvegarder la position de scroll avant la sauvegarde
        this.saveScrollPosition();
        
        // Récupérer le token depuis localStorage
        const userData = localStorage.getItem('user');
        if (!userData) {
          this.showNotification({ message: 'Session expirée, veuillez vous reconnecter', type: 'error' });
          this.$router.push('/login');
          return;
        }

        const userObj = JSON.parse(userData);
        const token = userObj.data.token;
        
        // Configurer les headers avec le token
        const config = {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        };

         const response = await axiosPeelo.post(
          `/strong/update/blockname/${this.$route.params.idchatbot}/${this.selectedBlock._id}/${this.selectedBlock.type}`,
          {
            blockname: this.selectedBlock.blockname
          },
          config
        );

        
        if (response.data.success) {
          // Mettre à jour le nom dans les listes de flux
          if (this.selectedBlock.category === 'primary') {
            const blockToUpdate = this.primaryBlocks.find(block => block._id === this.selectedBlock._id);
            if (blockToUpdate) {
              blockToUpdate.blockname = this.selectedBlock.blockname;
              blockToUpdate.displayName = this.selectedBlock.blockname;
            }
          } else {
            const blockToUpdate = this.secondaryBlocks.find(block => block._id === this.selectedBlock._id);
            if (blockToUpdate) {
              blockToUpdate.blockname = this.selectedBlock.blockname;
              blockToUpdate.displayName = this.selectedBlock.blockname;
            }
          }

          // Mettre à jour les listes filtrées
          this.filterBlocks();
          
          this.showNotification({ message: 'Sauvegardé', type: 'success' });
          this.isEditingBlockName = false;
          this.saveStatus = {
            show: true,
            type: 'saved',
            message: 'Sauvegardé'
          };
          
          // Restaurer la position de scroll après la sauvegarde réussie
          this.restoreScrollPosition();
        }
      } catch (error) {
        console.error('Erreur saveBlock:', error);
        
        if (error.response && (error.response.status === 401 || error.response.status === 403)) {
          this.showNotification({ message: 'Session expirée, veuillez vous reconnecter', type: 'error' });
          localStorage.removeItem('user');
          this.$router.push('/login');
          return;
        }

        this.showNotification({ message: 'Erreur lors de la sauvegarde', type: 'error' });
        this.saveStatus = {
          show: true,
          type: 'error',
          message: 'Erreur lors de la sauvegarde'
        };
        
        // Restaurer la position même en cas d'erreur
        this.restoreScrollPosition();
      }
    },
    
    // Méthode pour déclencher l'upload d'image
    uploadImage(index) {
      // Créer un élément input file dynamique
      const fileInput = document.createElement('input');
      fileInput.type = 'file';
      fileInput.accept = 'image/*';
      fileInput.style.display = 'none';
      
      // Ajouter l'événement de changement
      fileInput.addEventListener('change', (event) => {
        this.handleImageUpload(event, index);
      });
      
      // Ajouter au DOM, déclencher le clic, puis supprimer
      document.body.appendChild(fileInput);
      fileInput.click();
      setTimeout(() => {
        document.body.removeChild(fileInput);
      }, 1000);
    },
    
    // Méthode pour gérer l'upload d'image
    handleImageUpload(event, index) {
      const file = event.target.files[0];
      if (!file) return;
      
      // Vérifier que c'est bien une image
      if (!file.type.match('image.*')) {
        this.showNotification({
          message: "Veuillez sélectionner une image valide",
          type: "error"
        });
        return;
      }
      
      // Créer un URL pour l'image
      const imageUrl = URL.createObjectURL(file);
      
      // Mettre à jour l'élément avec le lien de l'image
      const element = this.selectedBlock.flow[index];
      element.link = imageUrl;
      
      // Dans une application réelle, vous voudriez probablement télécharger
      // l'image sur un serveur et stocker l'URL retournée
      
      this.saveBlock();
      
      this.showNotification({
        message: "Image ajoutée avec succès",
        type: "success"
      });
    },
    
    // Méthode pour afficher une notification
    showNotification({ message, type = 'success', duration = 5000 }) {
       if (this.notification.timeout) {
        clearTimeout(this.notification.timeout);
      }
      this.notification = {
        show: true,
        message,
        type,
        timeout: setTimeout(() => {
          this.notification.show = false;
        }, 3000)
      };
    },

    startEditingBlockName() {
      // Vérifier si l'utilisateur est connecté avant de permettre l'édition
      const userData = localStorage.getItem('user');
      if (!userData) {
        this.showNotification({ message: 'Session expirée, veuillez vous reconnecter', type: 'error' });
        this.$router.push('/login');
        return;
      }

      this.isEditingBlockName = true;
      this.editedBlockName = this.selectedBlock.blockname || '';
      this.$nextTick(() => {
        if (this.$refs.blockNameInput) {
          this.$refs.blockNameInput.focus();
        }
      });
    },

    saveBlockName() {
      this.isEditingBlockName = false;
      this.selectedBlock.blockname = this.editedBlockName;
      this.saveBlock();
    },

    cancelBlockNameEdit() {
      this.isEditingBlockName = false;
      this.editedBlockName = this.selectedBlock.blockname || '';
    },

    validateBlockName(event) {
      if (this.editedBlockName.length > 20) {
        this.editedBlockName = this.editedBlockName.slice(0, 20);
        this.showNotification({ message: 'Le nom ne peut pas dépasser 20 caractères', type: 'error' });
        return;
      }
    },

    async saveBlockName() {
      if (!this.editedBlockName.trim()) {
        this.showNotification({ message: 'Le nom du bloc ne peut pas être vide', type: 'error' });
        return;
      }
      
      if (this.editedBlockName.length > 20) {
        this.showNotification({ message: 'Le nom ne peut pas dépasser 20 caractères', type: 'error' });
        return;
      }

      this.selectedBlock.blockname = this.editedBlockName.trim();
      await this.saveBlock();
    },

    // Méthode pour obtenir le bon composant selon le type d'élément
    getElementComponent(type) {
      if (ELEMENT_COMPONENTS[type]) {
        return ELEMENT_COMPONENTS[type];
      }
      
      console.warn(`Type de composant non trouvé: ${type}. Types disponibles: ${Object.keys(ELEMENT_COMPONENTS).join(', ')}`);
      
      // Essayer de trouver un type similaire
      const normalizedType = type.toLowerCase().replace(/s$/, '');
      
      for (const [key, component] of Object.entries(ELEMENT_COMPONENTS)) {
        if (key.includes(normalizedType) || normalizedType.includes(key)) {
          console.info(`Utilisation du composant ${key} pour le type ${type}`);
          return component;
        }
      }
      
      return null;
    },

    // Méthode pour mettre à jour un élément depuis le composant
    updateElement(updatedElement, index) {
      if (this.selectedBlock && this.selectedBlock.flow && index >= 0 && index < this.selectedBlock.flow.length) {
        this.selectedBlock.flow[index] = { ...updatedElement };
        // Rafraîchir la chaîne de traçabilité (qui déclenche la sauvegarde)
        this.refreshChain();
      }
    },

    // Méthode pour dupliquer un élément
    duplicateElement(index) {
      if (this.selectedBlock && this.selectedBlock.flow && index >= 0 && index < this.selectedBlock.flow.length) {
        const elementToDuplicate = { ...this.selectedBlock.flow[index] };
        // Insérer après l'élément original
        this.selectedBlock.flow.splice(index + 1, 0, elementToDuplicate);
        // Rafraîchir la chaîne de traçabilité (qui déclenche la sauvegarde)
        this.refreshChain();
        this.showNotification({
          message: 'Élément dupliqué avec succès',
          type: 'success'
        });
      }
    },

    // Méthode pour récupérer les variables du chatbot
    async fetchVariables() {
      try {
        // Variables globales par défaut
        const globalVariables = [
          { id: 'first_name', type: 'global', slug: 'first_name' },
          { id: 'last_name', type: 'global', slug: 'last_name' },
          { id: 'reply_phone', type: 'global', slug: 'reply_phone' },
          { id: 'full_name', type: 'global', slug: 'full_name' },
          { id: 'phone_number', type: 'global', slug: 'phone_number' },
          { id: 'last_input', type: 'global', slug: 'last_input' },
          { id: 'id_user', type: 'global', slug: 'id_user' },
          { id: 'phone_number_id', type: 'global', slug: 'phone_number_id' },
          { id: 'bot_number', type: 'global', slug: 'bot_number' },
          { id: 'idbot', type: 'global', slug: 'idbot' },
          { id: 'order_amount', type: 'global', slug: 'order_amount' },


        ];
        
        const userData = localStorage.getItem('user');
        if (!userData) {
          this.variables = globalVariables;
          return;
        }
        
        const userObj = JSON.parse(userData);
        const token = userObj.data.token;
        const config = {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        };
        
        let chatbotVariables = [];
        
        try {
          const response = await axiosPeelo.get(
            `/get/strong/variables/${this.$route.params.idchatbot}`,
            config
          );
          
          if (response.data) {
            let responseVariables = null;
            
            if (response.data.success && response.data.variables) {
              responseVariables = response.data.variables;
            } else if (response.data.variables) {
              responseVariables = response.data.variables;
            } else if (Array.isArray(response.data)) {
              responseVariables = response.data;
            }
            
            if (responseVariables && Array.isArray(responseVariables)) {
              chatbotVariables = responseVariables;
            }
          }
        } catch (e) {
          console.error('Erreur lors de la récupération des variables:', e);
          chatbotVariables = [];
        }
        
        this.variables = [...globalVariables, ...chatbotVariables];
        
      } catch (e) {
        console.error('Erreur globale lors de la récupération des variables:', e);
        this.variables = [
          { id: 'first_name', type: 'global', slug: 'first_name' },
          { id: 'last_name', type: 'global', slug: 'last_name' },
          { id: 'reply_phone', type: 'global', slug: 'reply_phone' },
          { id: 'full_name', type: 'global', slug: 'full_name' },
          { id: 'phone_number', type: 'global', slug: 'phone_number' },
          { id: 'last_input', type: 'global', slug: 'last_input' },
          { id: 'id_user', type: 'global', slug: 'id_user' },
          { id: 'phone_number_id', type: 'global', slug: 'phone_number_id' },
          { id: 'bot_number', type: 'global', slug: 'bot_number' }
        ];
      }
    },

    // Navigation vers un bloc spécifique
    navigateToBlock(idblock) {
      this.$router.push(`/dashboard/chatbots/${this.$route.params.idchatbot}/playground/${idblock}`);
    },

    // Obtenir le label du type de bloc
    getBlockTypeLabel(type) {
      const labels = {
        welcome: 'Flux Principal',
        standard: 'Flux Standard',
        commerce: 'Commerce',
        default: 'Flux par défaut'
      };
      return labels[type] || type;
    },

    // Charger les liens entrants vers le bloc actuel
    async loadInboundLinks() {

      if (!this.selectedBlock) return;
      
      this.loadingInboundLinks = true;
      this.inboundLinks = [];
      
      try {
        const userData = localStorage.getItem('user');
        if (!userData) throw new Error('Données utilisateur non trouvées');
        
        const userObj = JSON.parse(userData);
        const token = userObj.data.token;
        const config = { headers: { 'Authorization': `Bearer ${token}` } };
        
        // Charger différents types de liens entrants
        const linkTypes = [ 'starter', 'commerce_point', 'default_block', 'blocks'];
        const allInboundLinks = [];
        
        for (const linkType of linkTypes) {
          try {
             
            const response = await axiosPeelo.get(
              `/get/flow/${this.$route.params.idchatbot}/${this.selectedBlock.idblock}/${linkType}`,
              config
            );
            
             
            if (response.data && response.data.match_previous && response.data.match_previous.length > 0) {
              // Ajouter le type de lien aux résultats pour identification
              const linksWithType = response.data.match_previous.map(link => ({
                ...link,
                linkType: linkType,
                sourceType: this.getLinkTypeLabel(linkType)
              }));
              allInboundLinks.push(...linksWithType);
            }
          } catch (error) {
            console.warn(`Erreur lors du chargement des liens ${linkType}:`, error);
            // Continue avec les autres types même si un échoue
          }
        }
        
        // Supprimer les doublons basés sur l'idblock
        const uniqueLinks = allInboundLinks.filter((link, index, self) => 
          index === self.findIndex(l => l.idblock === link.idblock)
        );
        
        this.inboundLinks = uniqueLinks;

        
        this.resetFilteredLinks();
      } catch (error) {
        console.error('Erreur lors du chargement des liens entrants:', error);
        this.showNotification({ 
          message: 'Erreur lors du chargement des liens entrants', 
          type: 'error' 
        });
      } finally {
        this.loadingInboundLinks = false;
      }
    },

    // Obtenir le label du type de lien
    getLinkTypeLabel(linkType) {
      const labels = {
        'previous': 'Bloc Standard',
        'starter': 'Flux de Démarrage',
        'commerce_point': 'Flux E-commerce',
        'default_block': 'Flux par Défaut'
      };
      return labels[linkType] || linkType;
    },

    // Analyser les liens sortants du bloc actuel
    analyzeOutboundLinks() {
      if (!this.selectedBlock || !this.selectedBlock.flow) {
        this.outboundLinks = [];
        return;
      }

      const outbound = [];
      
      this.selectedBlock.flow.forEach((element, index) => {
        // Vérifier les éléments de redirection
        if (element.type === 'redirect' || element.type === 'redirection') {
          if (element.redirection_block) {
            const targetBlock = this.blocks.find(block => block.idblock === element.redirection_block);
            if (targetBlock) {
              outbound.push({
                id: `redirect_${index}`,
                targetBlock: element.redirection_block,
                targetName: this.getBlockDisplayName(element.redirection_block),
                sourceType: 'Redirection'
              });
            }
          }
        }
        
        // Vérifier les boutons dans text_button en comparant l'id avec les idblock
        if (element.type === 'text_button' && element.buttons) {
          element.buttons.forEach((button, buttonIndex) => {
            if (button.id) {
              // Chercher si l'id du bouton correspond à un idblock d'un bloc existant
              const targetBlock = this.blocks.find(block => block.idblock === button.id);
              if (targetBlock) {
                outbound.push({
                  id: `button_${index}_${buttonIndex}`,
                  targetBlock: button.id,
                  targetName: this.getBlockDisplayName(button.id),
                  sourceType: `Bouton "${button.title}"`
                });
              }
            }
            
            // Fallback : vérifier aussi dans le payload pour les redirections explicites
            if (button.payload && button.payload.includes('redirect_to=')) {
              const match = button.payload.match(/redirect_to=([^&]+)/);
              if (match) {
                const targetBlock = this.blocks.find(block => block.idblock === match[1]);
                if (targetBlock) {
                  outbound.push({
                    id: `button_payload_${index}_${buttonIndex}`,
                    targetBlock: match[1],
                    targetName: this.getBlockDisplayName(match[1]),
                    sourceType: `Bouton "${button.title}" (payload)`
                  });
                }
              }
            }
          });
        }
        
        // Vérifier les listes interactives avec redirection
        if (element.type === 'list' && element.interactive && element.interactive.action && element.interactive.action.sections) {
          element.interactive.action.sections.forEach((section, sectionIndex) => {
            if (section.rows) {
              section.rows.forEach((row, rowIndex) => {
                // Vérifier si l'id de la row correspond à un idblock
                if (row.id) {
                  const targetBlock = this.blocks.find(block => block.idblock === row.id);
                  if (targetBlock) {
                    outbound.push({
                      id: `list_row_${index}_${sectionIndex}_${rowIndex}`,
                      targetBlock: row.id,
                      targetName: this.getBlockDisplayName(row.id),
                      sourceType: `Liste "${row.title}"`
                    });
                  }
                }
                
                // Fallback : vérifier aussi dans le payload
                if (row.payload && row.payload.includes('redirect_to=')) {
                  const match = row.payload.match(/redirect_to=([^&]+)/);
                  if (match) {
                    const targetBlock = this.blocks.find(block => block.idblock === match[1]);
                    if (targetBlock) {
                      outbound.push({
                        id: `list_payload_${index}_${sectionIndex}_${rowIndex}`,
                        targetBlock: match[1],
                        targetName: this.getBlockDisplayName(match[1]),
                        sourceType: `Liste "${row.title}" (payload)`
                      });
                    }
                  }
                }
              });
            }
          });
        }
      });
      
      // Supprimer les doublons basés sur targetBlock et sourceType
      const uniqueOutbound = outbound.filter((item, index, self) => 
        index === self.findIndex(t => t.targetBlock === item.targetBlock && t.sourceType === item.sourceType)
      );
      
      this.outboundLinks = uniqueOutbound;
      

      this.resetFilteredLinks();
    },

    // Obtenir le nom d'affichage d'un bloc par son ID
    getBlockDisplayName(idblock) {
      const block = this.blocks.find(b => b.idblock === idblock);
      return block ? (block.blockname || block.displayName || block.idblock) : idblock;
    },

    toggleLinksExpanded() {
      this.isLinksExpanded = !this.isLinksExpanded;
    },

    updateTotalLinksCount() {
      this.totalLinksCount = this.inboundLinks.length + this.outboundLinks.length;
    },

    getLinksStatus() {
      if (this.loadingInboundLinks) return 'loading';
      if (this.totalLinksCount === 0) return 'empty';
      if (this.totalLinksCount > 0) return 'active';
      return '';
    },

    getLinksStatusText() {
      const status = this.getLinksStatus();
      switch (status) {
        case 'loading':
          return 'Analyse...';
        case 'empty':
          return 'Isolé';
        case 'active':
          return this.totalLinksCount > 5 ? 'Connecté+' : 'Connecté';
        default:
          return '';
      }
    },

    toggleFilter() {
      this.showFilter = !this.showFilter;
      if (this.showFilter) {
        this.$nextTick(() => {
          if (this.$refs.filterInput) {
            this.$refs.filterInput.focus();
          }
        });
      } else {
        this.clearFilter();
      }
    },

    filterLinks() {
      const query = this.filterQuery.toLowerCase();
      
      this.filteredInboundLinks = this.inboundLinks.filter(link => 
        (link.blockname || link.idblock || '').toLowerCase().includes(query)
      );
      
      this.filteredOutboundLinks = this.outboundLinks.filter(link => 
        (link.targetName || link.targetBlock || '').toLowerCase().includes(query) ||
        (link.sourceType || '').toLowerCase().includes(query)
      );
    },

    clearFilter() {
      this.filterQuery = '';
      this.resetFilteredLinks();
    },

    resetFilteredLinks() {
      this.filteredInboundLinks = [...this.inboundLinks];
      this.filteredOutboundLinks = [...this.outboundLinks];
    },

    // Générer une chaîne alphanumérique aléatoire
    generateRandomId(length = 8) {
      const chars = 'abcdefghijklmnopqrstuvwxyz0123456789';
      let result = '';
      for (let i = 0; i < length; i++) {
        result += chars.charAt(Math.floor(Math.random() * chars.length));
      }
      return result;
    },

    // Rafraîchir la chaîne de traçabilité du flow
    refreshChain() {
      if (!this.selectedBlock || !this.selectedBlock.flow) return;
      

      this.loading = true;
      
      try {
        const updatedFlow = this.selectedBlock.flow.map((element, index) => {
          return this.updateElementChain(element, index);
        });
        
        this.selectedBlock.flow = updatedFlow;

        
        // Sauvegarder automatiquement après la mise à jour
        this.saveFlowToServer();
      } catch (error) {
        console.error('Erreur lors de la mise à jour de la chaîne:', error);
      } finally {
        this.loading = false;
      }
    },

    // Vérifier la connexion internet
    checkConnection() {
      return navigator.onLine;
    },

    // Sauvegarder le flow sur le serveur
    async saveFlowToServer() {
      if (!this.selectedBlock || !this.selectedBlock.flow) return;
      
      // Sauvegarder la position de scroll avant la sauvegarde
      this.saveScrollPosition();
      
      // Vérifier la connexion
      if (!this.checkConnection()) {
        this.showNotification({
          message: 'Pas de connexion, vos changements ne seront pas effectués',
          type: 'error',
          duration: 5000
        });
        this.restoreScrollPosition();
        return;
      }
      
      // Afficher le statut "sauvegarde en cours"
      this.showNotification({
        message: 'Sauvegarde...',
        type: 'saving',
        duration: 0 // Pas d'auto-hide
      });
      
      try {
        const userData = localStorage.getItem('user');
        if (!userData) {
          throw new Error('Session expirée');
        }
        
        const userObj = JSON.parse(userData);
        const token = userObj.data.token;
        const config = { headers: { 'Authorization': `Bearer ${token}` } };
        
        // Déterminer le type de bloc
        const blockType = this.selectedBlock.type || 'blocks';
        
         // Debug log supprimé pour éviter les surcharges
        const response = await axiosPeelo.post(
          `/update/flow/${this.$route.params.idchatbot}/${this.selectedBlock._id}/${blockType}`,
          { flow: this.selectedBlock.flow },
          config
        );
        
        if (response.data.success) {
          this.showNotification({
            message: 'Saved',
            type: 'saved',
            duration: 2000
          });
          
          // Restaurer la position de scroll après la sauvegarde réussie
          this.restoreScrollPosition();
        } else {
          throw new Error(response.data.error || 'Erreur lors de la sauvegarde');
        }
      } catch (error) {
        console.error('Erreur lors de la sauvegarde du flow:', error);
        this.showNotification({
          message: 'Erreur de sauvegarde',
          type: 'error',
          duration: 4000
        });
        
        // Restaurer la position même en cas d'erreur
        this.restoreScrollPosition();
      }
    },

    // Méthode pour afficher les notifications unifiée
    showNotification({ message, type = 'success', duration = 3000 }) {
       if (this.notification.timeout) {
        clearTimeout(this.notification.timeout);
      }
      
      this.notification = {
        show: true,
        message,
        type,
        timeout: duration > 0 ? setTimeout(() => {
          this.notification.show = false;
        }, duration) : null
      };
    },

    // Mettre à jour la chaîne d'un élément spécifique
    updateElementChain(element, index) {
      if (!element || !this.selectedBlock) return element;
      
      // Générer l'id_element : ${idbot}_${block._id}_${element.type}_${random}
      const idbot = this.$route.params.idchatbot;
      const blockId = this.selectedBlock._id;
      const elementType = element.type || 'unknown';
      const randomId = this.generateRandomId();
      
      element.id_element = `${idbot}_${blockId}_${elementType}_${randomId}`;
      
      // Définir id_previous : null pour le premier élément, sinon l'id_element du précédent
      element.id_previous = index === 0 ? null : this.selectedBlock.flow[index - 1].id_element;
      
      return element;
    },

    handleOnline() {
      this.isOnline = true;
      this.showNotification({ message: 'Connexion rétablie', type: 'success' });
    },

    handleOffline() {
      this.isOnline = false;
      this.showNotification({ message: 'Connexion perdue', type: 'error' });
    },

    copyIdBlock() {
      const textToCopy = this.selectedBlock.idblock;
      const tempInput = document.createElement('input');
      tempInput.value = textToCopy;
      document.body.appendChild(tempInput);
      tempInput.select();
      document.execCommand('copy');
      document.body.removeChild(tempInput);
      this.showNotification({ message: 'ID copié avec succès', type: 'success' });
    },

    // Méthodes pour gérer la position de scroll
    saveScrollPosition() {
      const workspace = document.querySelector('.workspace');
      if (workspace) {
        this.scrollPosition = workspace.scrollTop;
      }
    },

    restoreScrollPosition() {
      this.$nextTick(() => {
        const workspace = document.querySelector('.workspace');
        if (workspace && this.scrollPosition !== undefined) {
          workspace.scrollTop = this.scrollPosition;
        }
      });
    },

    async updateJarvisState() {
      try {
        // Sauvegarder la position de scroll avant la sauvegarde
        this.saveScrollPosition();
        
        const userData = localStorage.getItem('user');
        if (!userData) throw new Error('Session expirée');
        
        const userObj = JSON.parse(userData);
        const token = userObj.data.token;
        
        const response = await axiosPeelo.post(`/strong/update/jarvis/${this.selectedBlock.idblock}`, {
          status: this.selectedBlock.jarvis
        }, {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });
        
        if (response.data.success) {
          this.showNotification({
            message: response.data.message,
            type: 'success'
          });
        } else {
          this.showNotification({
            message: response.data.message || 'Erreur lors de la mise à jour de Jarvis',
            type: 'error'
          });
        }
        
        // Restaurer la position de scroll après la sauvegarde
        this.restoreScrollPosition();
      } catch (error) {
        this.showNotification({
          message: `Erreur: ${error.response?.data?.error || 'Erreur lors de la mise à jour de Jarvis'}`,
          type: 'error'
        });
        console.error('Erreur lors de la mise à jour de Jarvis:', error);
        
        // Restaurer la position même en cas d'erreur
        this.restoreScrollPosition();
      }
    },
  },
  computed: {
    jarvisToggleDataAttr() {
      return {
        'data-jarvis': this.selectedBlock.jarvis
      };
    }
  },
};
</script>

<style scoped>
.playground-container {
  display: flex;
  height: 100vh;
  width: 100vw;
  background-color: #ffffff;
  position: fixed;
  left: 0;
  top: 0;
  right: 0;
  bottom: 0;
  z-index: 9999;
}

.sidebar {
  width: 340px;
  background: #0E8A3E; /* Couleur verte plus foncée */
  border-right: 1px solid rgba(255, 255, 255, 0.1);
  display: flex;
  flex-direction: column;
  transition: width 0.3s ease;
  overflow-y: auto;
  font-family: system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
  color: white;
}

.sidebar.expanded {
  width: 350px;
}

.sidebar-header {
  padding: 1.5rem;
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
}

.chatbot-info {
  padding: 0 0 1rem 0;
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
  margin-bottom: 1rem;
  text-align: left;
  margin-left: 0;
  padding-left: 0;
}

.chatbot-name {
  font-size: 1.1rem;
  font-weight: 600;
  color: white;
  text-decoration: none;
  display: block;
  margin-bottom: 0.5rem;
  transition: opacity 0.2s ease;
}

.chatbot-name:hover {
  opacity: 0.8;
}

.chatbot-description {
  font-size: 0.85rem;
  color: rgba(255, 255, 255, 0.8);
  margin: 0;
  line-height: 1.4;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  text-overflow: ellipsis;
}

.header-title {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1rem;
  padding: 0.5rem 0;
}

.logo {
  height: 24px;
  display: flex;
  align-items: center;
}

.logo img {
  height: 100%;
  object-fit: contain;
  width: auto;
  opacity: 0.9;
}

.add-btn {
  background: rgba(255, 255, 255, 0.1);
  border: none;
  width: 32px;
  height: 32px;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  color: white;
  transition: all 0.2s ease;
  position: relative;
}

.add-btn:hover {
  background: rgba(255, 255, 255, 0.2);
}

.add-btn .tooltip-text {
  visibility: hidden;
  position: absolute;
  left: calc(100% + 12px);
  top: 50%;
  transform: translateY(-50%);
  background-color: #1F2937;
  color: white;
  padding: 8px 12px;
  border-radius: 6px;
  font-size: 12px;
  font-weight: 500;
  white-space: nowrap;
  pointer-events: none;
  opacity: 0;
  transition: opacity 0.2s ease, visibility 0.2s ease;
  z-index: 10000;
}

.add-btn .tooltip-text::before {
  content: '';
  position: absolute;
  top: 50%;
  right: 100%;
  transform: translateY(-50%);
  border-width: 6px;
  border-style: solid;
  border-color: transparent #1F2937 transparent transparent;
}

.add-btn:hover .tooltip-text,
.add-btn .tooltip-text.visible {
  visibility: visible;
  opacity: 1;
}

.search-container {
  position: relative;
}

.search-input {
  display: flex;
  align-items: center;
  background: rgba(255, 255, 255, 0.1);
  border-radius: 8px;
  padding: 0.5rem;
}

.search-input input {
  flex: 1;
  background: none;
  border: none;
  color: white;
  padding: 0.5rem;
  font-size: 0.9rem;
}

.search-input input::placeholder {
  color: rgba(255, 255, 255, 0.6);
}

.search-input svg {
  color: rgba(255, 255, 255, 0.6);
  margin: 0 0.5rem;
}

.folder-section {
  margin: 1.5rem 0;
}

.folder-header {
  display: flex;
  align-items: center;
  padding: 0.75rem 1rem;
  cursor: pointer;
  color: rgba(255, 255, 255, 0.8);
  transition: all 0.2s ease;
}

.folder-header:hover {
  background: rgba(255, 255, 255, 0.1);
}

.folder-header svg {
  margin-right: 0.75rem;
  transition: transform 0.2s ease;
}

.folder-header svg.rotated {
  transform: rotate(-90deg);
}

.folder-content {
  padding: 0.75rem;
  display: flex;
  flex-wrap: wrap;
  gap: 0.75rem;
}

.flow-button {
  display: inline-flex;
  align-items: center;
  padding: 0.75rem 1.25rem;
  background: rgba(255, 255, 255, 0.1);
  border-radius: 6px;
  color: white;
  cursor: pointer;
  transition: all 0.2s ease;
  font-size: 0.85rem;
  height: 42px;
  white-space: nowrap;
  flex: 0 1 auto;
  min-width: fit-content;
}

.flow-button:not(.active):hover {
  background: rgba(255, 255, 255, 0.2);
  transform: translateY(-1px);
}

.flow-button.active {
  background: rgba(255, 255, 255, 0.25);
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  position: relative;
  z-index: 1;
}

.flow-button.add-flow {
  border: 1px dashed rgba(255, 255, 255, 0.3);
  color: rgba(255, 255, 255, 0.7);
  margin-left: auto;
}

.flow-button.add-flow:hover {
  border-color: white;
  color: white;
}

.new-folder-button {
  display: flex;
  align-items: center;
  padding: 0.75rem 1rem;
  color: #64748b;
  cursor: pointer;
  margin-top: 1rem;
}

.new-folder-button svg {
  margin-right: 0.5rem;
  color: #94a3b8;
}

.new-folder-button:hover {
  color: #334155;
  background-color: #f8fafc;
}

/* Override the global main-content style specifically for this component */
.playground-container .main-content {
  flex: 1;
  padding: 0;
  overflow-y: auto;
  margin-left: 0 !important; /* Remove default left margin with !important to override global styles */
  display: flex;
  flex-direction: column;
}

.notification-banner {
  background-color: #fff3e0;
  padding: 0.75rem 1rem;
  display: flex;
  justify-content: space-between;
  align-items: center;
  color: #e65100;
  font-size: 0.9rem;
}

.connect-btn {
  background-color: #ff9800;
  color: white;
  border: none;
  border-radius: 4px;
  padding: 0.5rem 1rem;
  font-weight: 500;
  cursor: pointer;
}

.workspace {
  flex: 1;
  position: relative;
  padding: 3rem 2rem;
  background: white;
  border-radius: 12px;
  margin: 1rem;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.05);
  width: 100%;
  height: 100%;
  overflow-y: auto;
  overflow-x: visible;
}

.block-header {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 1rem 0;
  background: transparent;
}

.block-name-container {
  position: relative;
  display: flex;
  align-items: center;
  max-width: 500px; /* Add max-width to limit the container size */
  width: fit-content; /* Make the container fit its content */
}

.block-name {
  font-size: 24px;
  font-weight: 500;
  color: #2c3e50;
  cursor: text;
}

.block-name.no-name {
  color: #999;
  font-style: italic;
}

.block-name-input {
  font-size: 24px;
  font-weight: 500;
  color: #2c3e50;
  background: transparent;
  border: none;
  outline: none;
  padding: 0;
  margin: 0;
  width: 100%;
  max-width: 500px; /* Add max-width to match container */
  font-family: inherit;
  -webkit-appearance: none;
  appearance: none;
  outline: none !important;
}

.block-name-input:focus,
.block-name-input:active,
.block-name-input:hover,
.block-name-input:-moz-focusring,
.block-name-input:focus-visible,
.block-name-input:focus-within,
.block-name-input:visited {
  outline: none !important;
  border: none !important;
  box-shadow: none !important;
  background: transparent !important;
  -webkit-appearance: none !important;
  appearance: none !important;
}

/* Supprimer la bordure de focus sur Firefox */
.block-name-input::-moz-focus-inner {
  border: 0 !important;
  outline: none !important;
}

/* Supprimer la bordure de focus sur Chrome */
.block-name-input:focus-visible {
  outline: none !important;
  outline-offset: 0 !important;
}

/* Supprimer la bordure de focus sur Safari */
.block-name-input:focus {
  outline: none !important;
  -webkit-focus-ring-color: transparent !important;
}

/* Supprimer la bordure de focus sur IE/Edge */
.block-name-input::-ms-clear,
.block-name-input::-ms-reveal {
  display: none !important;
}

.block-name-input:-ms-input-placeholder {
  color: transparent !important;
}

/* Supprimer l'effet de focus lors du tab */
.block-name-input:not(:focus-visible) {
  outline: none !important;
}

.char-count {
  position: absolute;
  right: -45px;
  top: 50%;
  transform: translateY(-50%);
  font-size: 12px;
  color: #999;
  opacity: 0.7;
}

.char-count-limit {
  color: #ff4444;
}

.block-header svg {
  color: #666;
  opacity: 0.7;
}

.block-info {
  background: white;
  border-radius: 16px;
  padding: 1.5rem;
  margin-bottom: 2rem;
  border: none;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.08);
}

.info-row {
  display: flex;
  margin-bottom: 0.75rem;
  font-size: 0.9rem;
}

.info-label {
  font-weight: 600;
  width: 120px;
  color: #667eea;
}

.info-value {
  color: #333;
  flex: 1;
  font-weight: 500;
}

.flow-title {
  text-align: center;
  margin: 3rem 0 2rem;
  color: #333;
  font-size: 1.3rem;
  font-weight: 700;
  position: relative;
}

.flow-title::after {
  content: '';
  position: absolute;
  bottom: -8px;
  left: 50%;
  transform: translateX(-50%);
  width: 60px;
  height: 3px;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  border-radius: 2px;
}

.flow-empty {
  background: white;
  border-radius: 16px;
  padding: 3rem 2rem;
  text-align: center;
  color: #666;
  font-style: italic;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.08);
  border: 2px dashed #e0e0e0;
}

.add-first-element {
  margin-top: 1.5rem;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  border: none;
  padding: 0.75rem 1.5rem;
  border-radius: 25px;
  cursor: pointer;
  transition: all 0.3s ease;
  font-weight: 600;
  box-shadow: 0 4px 15px rgba(102, 126, 234, 0.3);
}

.add-first-element:hover {
  transform: translateY(-2px);
  box-shadow: 0 6px 20px rgba(102, 126, 234, 0.4);
}

.start-node {
  top: 250px;
  left: 100px;
}

.node-header {
  padding: 0.75rem;
  border-bottom: 1px solid #e0e0e0;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  background: #f8f9fa;
}

.element-header {
  display: none; /* Masquer l'en-tête pour un look plus propre */
}

.element-type {
  font-weight: 500;
  color: #555;
  font-size: 0.9rem;
  display: none;
}

.element-actions {
  position: absolute;
  top: 1rem;
  right: 1rem;
  display: flex;
  gap: 0.5rem;
  opacity: 0;
  transition: opacity 0.2s ease;
  z-index: 2;
}

.flow-element:hover .element-actions {
  opacity: 1;
}

.element-actions .action-btn {
  background: white;
  border: none;
  cursor: pointer;
  padding: 0.5rem;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s ease;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  width: 36px;
  height: 36px;
}

.element-actions .action-btn:hover {
  transform: scale(1.1);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
}

.element-actions .edit-btn svg {
  color: #667eea;
}

.element-actions .delete-btn svg {
  color: #ff6b6b;
}

.element-content {
  padding: 0;
  display: block;
  position: static;
}

.element-footer {
  display: none; /* Masquer le footer pour un look plus propre */
}

/* Styles spécifiques par type d'élément - couleurs plus subtiles */
.element-type-text {
  background: linear-gradient(135deg, #f8f9ff 0%, #f0f4ff 100%);
  border: 1px solid #e0e7ff;
}

.element-type-text_button {
  background: linear-gradient(135deg, #fef7ff 0%, #f3e8ff 100%);
  border: 1px solid #e9d5ff;
}

.element-type-list {
  background: linear-gradient(135deg, #fff8f0 0%, #fef3e2 100%);
  border: 1px solid #fed7aa;
}

.element-type-image {
  background: linear-gradient(135deg, #f0fdf4 0%, #dcfce7 100%);
  border: 1px solid #bbf7d0;
}

.element-type-audio {
  background: linear-gradient(135deg, #ecfeff 0%, #cffafe 100%);
  border: 1px solid #a5f3fc;
}

.element-type-delay {
  background: linear-gradient(135deg, #fafafa 0%, #f4f4f5 100%);
  border: 1px solid #e4e4e7;
}

.element-type-request {
  background: linear-gradient(135deg, #eff6ff 0%, #dbeafe 100%);
  border: 1px solid #93c5fd;
}

.element-type-answer {
  background: linear-gradient(135deg, #fffbeb 0%, #fef3c7 100%);
  border: 1px solid #fde68a;
  overflow: visible !important;
  z-index: 100 !important;
}

.element-type-redirect {
  background: linear-gradient(135deg, #fef2f2 0%, #fecaca 100%);
  border: 1px solid #fca5a5;
}

.element-type-notification {
  background: linear-gradient(135deg, #fdf2f8 0%, #fce7f3 100%);
  border: 1px solid #f9a8d4;
}

.element-type-variable_insert {
  background: linear-gradient(135deg, #f0fdf4 0%, #dcfce7 100%);
  border: 1px solid #86efac;
}

.element-type-document {
  background: linear-gradient(135deg, #fef2f2 0%, #fee2e2 100%);
  border: 1px solid #fca5a5;
}

/* Styles pour le nouveau wrapper d'éléments */
.flow-element-wrapper {
  margin: 4rem auto;
  background: white;
  border-radius: 20px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
  border: none;
  overflow: visible;
  transition: all 0.3s ease;
  cursor: grab;
  width: 90%;
  max-width: 600px;
  padding: 0;
  position: relative;
  z-index: 1;
}

.flow-element-wrapper:hover {
  transform: translateY(-4px);
  box-shadow: 0 8px 30px rgba(0, 0, 0, 0.12);
}

.flow-element-wrapper:has(.multiselect--active) {
  z-index: 9998 !important;
  overflow: visible !important;
}

.flow-element-wrapper .multiselect--active {
  z-index: 9999 !important;
}

.flow-element-wrapper:active {
  cursor: grabbing;
}

.flow-element-wrapper.dragging {
  opacity: 0.8;
  transform: scale(1.05) translateY(-8px);
  box-shadow: 0 12px 40px rgba(0, 0, 0, 0.2);
}

.flow-element-wrapper.drag-over {
  border: 3px dashed #667eea;
  transform: scale(1.02);
}

.flow-element-wrapper .element-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 12px 16px;
  background: #f9fafb;
  border-bottom: 1px solid #e5e7eb;
}

.flow-element-wrapper .element-type {
  font-weight: 500;
  color: #555;
  font-size: 0.9rem;
}

.flow-element-wrapper .element-actions {
  display: flex;
  gap: 0.5rem;
  opacity: 0;
  transition: opacity 0.2s ease;
  z-index: 2;
}

.flow-element-wrapper:hover .element-actions {
  opacity: 1;
}

.flow-element-wrapper .element-actions .action-btn {
  background: white;
  border: none;
  cursor: pointer;
  padding: 0.5rem;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s ease;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  width: 36px;
  height: 36px;
}

.flow-element-wrapper .element-actions .action-btn:hover {
  transform: scale(1.1);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
}

.flow-element-wrapper .element-actions .duplicate-btn svg {
  color: #667eea;
}

.flow-element-wrapper .element-actions .delete-btn svg {
  color: #ff6b6b;
}

.flow-element-wrapper .element-actions .delete-btn:hover {
  background: #fee2e2;
  border-color: #fca5a5;
  color: #ef4444;
}

/* Styles pour le composant dynamique */
.dynamic-element {
  padding: 0;
  margin: 0;
  position: relative;
  z-index: auto;
}

/* Styles pour l'élément fallback */
.fallback-element {
  padding: 2rem;
  text-align: center;
  background: linear-gradient(135deg, #fee2e2 0%, #fecaca 100%);
  border: 2px dashed #fca5a5;
  border-radius: 12px;
  margin: 1rem;
  color: #dc2626;
}

.fallback-header {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.75rem;
  margin-bottom: 1rem;
  font-weight: 600;
  font-size: 1.1rem;
}

.fallback-element p {
  margin: 0.5rem 0;
  font-size: 0.9rem;
}

.element-debug-details {
  margin-top: 1rem;
  text-align: left;
}

.element-debug-details summary {
  cursor: pointer;
  font-weight: 500;
  padding: 0.5rem;
  color: #333;
}

.block-name.no-name {
  color: #999;
}

.block-name-input {
  width: 100%;
  padding: 0;
  border: 0px solid #e0e0e0;
  font-weight: 500;
  border-radius: 4px;
  font-size: 24px;
}

.block-name-input:focus {
  outline: none;
  
}

.element-previous {
  font-style: italic;
}

/* Animation d'apparition pour les éléments */
@keyframes elementAppear {
  from {
    opacity: 0;
    transform: translateY(20px) scale(0.95);
  }
  to {
    opacity: 1;
    transform: translateY(0) scale(1);
  }
}

.flow-element-wrapper {
  animation: elementAppear 0.4s ease-out;
}

/* Amélioration de la scrollbar */
.workspace::-webkit-scrollbar {
  width: 8px;
}

.workspace::-webkit-scrollbar-track {
  background: rgba(255, 255, 255, 0.1);
  border-radius: 10px;
}

.workspace::-webkit-scrollbar-thumb {
  background: rgba(102, 126, 234, 0.3);
  border-radius: 10px;
}

.workspace::-webkit-scrollbar-thumb:hover {
  background: rgba(102, 126, 234, 0.5);
}

.tools-sidebar {
  position: fixed;
  right: 1rem;
  top: 50%;
  transform: translateY(-50%);
  width: 60px;
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  background: white;
  padding: 1rem 0.5rem;
  border-radius: 12px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  max-height: 80%;
  overflow-y: auto;
  z-index: 1000;
}

.tool-btn {
  width: 40px;
  height: 40px;
  border-radius: 8px;
  border: 1px solid #e0e0e0;
  background: white;
  color: #666;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.2s ease;
  position: relative;
  flex-shrink: 0;
}

.tool-btn:hover {
  background: #f5f5f5;
  color: #1EA355;
  border-color: #1EA355;
  transform: translateX(-2px);
}

.test-flow-btn {
  position: absolute;
  bottom: 1rem;
  right: 1rem;
  background: #1EA355;
  color: white;
  padding: 0.75rem 1.5rem;
  border-radius: 8px;
  display: flex;
  align-items: center;
  gap: 0.75rem;
  cursor: pointer;
  transition: all 0.2s ease;
  border: none;
  font-weight: 500;
}

.test-flow-btn:hover {
  background: #167F42;
  transform: translateY(-1px);
}

.btn-dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: white;
}

/* Loading and Error States with green theme */
.loading-state,
.error-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 2rem;
  text-align: center;
  color: rgba(255, 255, 255, 0.9);
}

.loading-spinner svg {
  color: white;
}

.retry-btn {
  margin-top: 1rem;
  padding: 0.75rem 1.5rem;
  background: rgba(255, 255, 255, 0.2);
  color: white;
  border: none;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.2s ease;
}

.retry-btn:hover {
  background: rgba(255, 255, 255, 0.3);
}

.empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 100%;
  color: #666;
  text-align: center;
  padding: 2rem;
}

.empty-state svg {
  margin-bottom: 1rem;
  opacity: 0.5;
}

.empty-state p {
  font-size: 0.9rem;
  max-width: 300px;
  line-height: 1.4;
}

.back-button {
  position: absolute;
  top: 1rem;
  right: 1rem;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.75rem 1rem;
  background: white;
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  color: #1EA355;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  z-index: 10;
}

.back-button:hover {
  background: #f5f5f5;
  transform: translateY(-1px);
}

.back-button svg {
  width: 16px;
  height: 16px;
}

/* Styles des notifications */
.notification-wrapper {
  position: fixed;
  bottom: 24px;
  right: 24px;
  z-index: 10000;
  animation: slideIn 0.3s ease forwards;
}

@keyframes slideIn {
  from {
    transform: translateY(100px);
    opacity: 0;
  }
  to {
    transform: translateY(0);
    opacity: 1;
  }
}

.notification {
  position: fixed;
  top: 20px;
  right: 20px;
  padding: 15px 25px;
  border-radius: 8px;
  z-index: 1000;
  animation: slideIn 0.3s ease-out;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
}

.notification.success {
  background-color: #4CAF50;
  color: white;
}

.notification.error {
  background-color: #f44336;
  color: white;
}

.notification span {
  font-size: 14px;
  font-weight: 500;
}

/* Modal styles */
.modal {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 10000;
}

.modal-backdrop {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.5);
}

.modal-content {
  background: white;
  border-radius: 12px;
  width: 100%;
  max-width: 500px;
  position: relative;
  z-index: 1;
  box-shadow: 0 10px 25px rgba(0, 0, 0, 0.15);
  overflow: hidden;
}

.modal-header {
  padding: 1.5rem;
  border-bottom: 1px solid #e0e0e0;
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.modal-header h3 {
  margin: 0;
  color: #333;
  font-size: 1.25rem;
}

.close-btn {
  background: none;
  border: none;
  cursor: pointer;
  color: #666;
  padding: 0.5rem;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s ease;
}

.close-btn:hover {
  background: #f5f5f5;
  color: #333;
}

.modal-body {
  padding: 1.5rem;
}

.form-group {
  margin-bottom: 1.5rem;
  position: relative;
}

.char-count {
  position: absolute;
  right: -30px;
  top: 50%;
  transform: translateY(-50%);
  font-size: 12px;
  color: #666;
  opacity: 0.7;
}

.char-count-limit {
  color: #f44336;
  font-weight: bold;
}

.form-group label {
  display: block;
  margin-bottom: 0.5rem;
  font-weight: 500;
  color: #333;
}

.form-group input {
  width: 100%;
  padding: 0.75rem;
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  font-size: 1rem;
  transition: all 0.2s ease;
}

.form-group input:focus {
  outline: none;
  border-color: #1EA355;
  box-shadow: 0 0 0 2px rgba(30, 163, 85, 0.1);
}

.form-group input:disabled {
  background: #f9f9f9;
  color: #666;
}

.modal-footer {
  padding: 1.5rem;
  border-top: 1px solid #e0e0e0;
  display: flex;
  justify-content: flex-end;
  gap: 1rem;
}

.cancel-btn {
  padding: 0.75rem 1.5rem;
  background: white;
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  color: #666;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
}

.cancel-btn:hover {
  background: #f5f5f5;
}

.create-btn {
  padding: 0.75rem 1.5rem;
  background: #1EA355;
  border: none;
  border-radius: 8px;
  color: white;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
}

.create-btn:hover:not(:disabled) {
  background: #167F42;
}

.create-btn:disabled {
  background: #a0d8b8;
  cursor: not-allowed;
}

/* Styles du tooltip */
.add-btn, .tool-btn {
  position: relative;
}

.tooltip-text {
  visibility: hidden;
  position: fixed;
  left: 110%;
  top: 50%;
  transform: translateY(-50%);
  background-color: #1F2937;
  color: white;
  padding: 6px 10px;
  border-radius: 6px;
  font-size: 12px;
  white-space: nowrap;
  pointer-events: none;
  opacity: 0;
  transition: opacity 0.2s ease, visibility 0.2s ease;
  z-index: 40000000000;
}

.tooltip-text::before {
  content: '';
  position: absolute;
  top: 50%;
  right: 100%;
  transform: translateY(-50%);
  border-width: 6px;
  border-style: solid;
  border-color: transparent #1F2937 transparent transparent;
}

.tooltip-text.visible {
  position: fixed !important;
  z-index: 99999 !important;
  visibility: visible !important;
  opacity: 1 !important;
}

/* Styles du panneau d'information dynamique */
.element-info-panel {
  position: fixed;
  right: 100px;
  top: 50%;
  transform: translateY(-50%);
  background: white;
  padding: 1.5rem;
  border-radius: 12px;
  min-width: 280px;
  max-width: 400px;
  box-shadow: 0 10px 25px rgba(0, 0, 0, 0.15);
  border: 1px solid #e0e0e0;
  z-index: 10000;
  animation: slideInRight 0.2s ease-out;
  pointer-events: none;
}

@keyframes slideInRight {
  from {
    opacity: 0;
    transform: translateY(-50%) translateX(20px);
  }
  to {
    opacity: 1;
    transform: translateY(-50%) translateX(0);
  }
}

.element-info-panel h4 {
  margin: 0 0 0.75rem 0;
  font-size: 1rem;
  color: #1EA355;
  font-weight: 600;
}

.element-info-panel p {
  margin: 0;
  font-size: 0.85rem;
  color: #666;
  line-height: 1.4;
}

.block-name-container {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.block-name {
  cursor: pointer;
  font-weight: 500;
  color: #333;
}

.block-name.no-name {
  color: #999;
}

.block-name-input {
  width: 100%;
  padding: 0;
  border: 0px solid #e0e0e0;
  font-weight: 500;
  border-radius: 4px;
  font-size: 24px;
}

.block-name-input:focus {
  outline: none;
}

.links-card {
  background: transparent;
  border: none;
  margin-bottom: 2rem;
  overflow: visible;
}

.links-card .links-header {
  display: inline-flex;
  justify-content: space-between;
  align-items: center;
  padding: 0.4rem 0.8rem;
  background: rgba(255, 255, 255, 0.1);
  backdrop-filter: blur(10px);
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 20px;
  cursor: pointer;
  transition: all 0.3s ease;
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.1);
  width: fit-content;
  margin-bottom: 0.75rem;
}

.links-card .links-header:hover {
  background: rgba(255, 255, 255, 0.15);
  border-color: rgba(255, 255, 255, 0.3);
  transform: translateY(-1px);
  box-shadow: 0 6px 20px rgba(0, 0, 0, 0.15);
}

.links-card .links-header .links-header-content {
  display: flex;
  align-items: center;
  gap: 0.6rem;
}

.links-card .links-header .links-header-content svg {
  color: #667eea;
  width: 16px;
  height: 16px;
  filter: drop-shadow(0 1px 2px rgba(0, 0, 0, 0.1));
}

.links-card .links-header .links-title-text {
  font-size: 0.85rem;
  font-weight: 600;
  color: #334155;
  text-shadow: 0 1px 2px rgba(255, 255, 255, 0.5);
}

.links-card .links-header .links-summary {
  background: linear-gradient(135deg, rgba(102, 126, 234, 0.8), rgba(118, 75, 162, 0.8));
  color: white;
  padding: 0.25rem 0.5rem;
  border-radius: 12px;
  font-size: 0.75rem;
  font-weight: 600;
  min-width: 20px;
  text-align: center;
  box-shadow: 0 2px 8px rgba(102, 126, 234, 0.3);
  backdrop-filter: blur(5px);
}

.links-card .links-header .collapse-icon {
  color: #64748b;
  transition: all 0.3s ease;
  width: 16px;
  height: 16px;
  filter: drop-shadow(0 1px 2px rgba(0, 0, 0, 0.1));
}

.links-card .links-header .collapse-icon.expanded {
  transform: rotate(180deg);
  color: #667eea;
}

.links-card .links-content {
  padding: 0;
  background: rgba(255, 255, 255, 0.05);
  backdrop-filter: blur(10px);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 16px;
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.08);
}

.links-card .links-group {
  margin-bottom: 1rem;
  padding: 1rem;
}

.links-card .links-group:last-child {
  margin-bottom: 0;
}

.links-card .links-group .group-header {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin-bottom: 0.75rem;
  color: #64748b;
  font-size: 0.8rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.links-card .links-group .group-header svg {
  width: 12px;
  height: 12px;
}

.links-card .loading-state {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 1rem;
  color: #64748b;
  font-size: 0.85rem;
  justify-content: center;
}

.links-card .loading-spinner {
  width: 16px;
  height: 16px;
  border: 2px solid rgba(255, 255, 255, 0.2);
  border-top: 2px solid #667eea;
  border-radius: 50%;
  animation: spin 1s linear infinite;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

.links-card .empty-state {
  padding: 0.75rem;
  text-align: center;
  color: #94a3b8;
  font-size: 0.8rem;
  font-style: italic;
}

.links-card .links-grid {
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
}

.links-card .link-badge {
  display: inline-flex;
  flex-direction: column;
  align-items: flex-start;
  padding: 0.5rem 0.75rem;
  background: rgba(255, 255, 255, 0.1);
  backdrop-filter: blur(8px);
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 12px;
  cursor: pointer;
  transition: all 0.3s ease;
  font-size: 0.8rem;
  max-width: 150px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
}

.links-card .link-badge:hover {
  background: rgba(255, 255, 255, 0.15);
  border-color: rgba(255, 255, 255, 0.3);
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
}

.links-card .link-badge.inbound {
  border-left: 4px solid #10b981;
  background: linear-gradient(135deg, rgba(16, 185, 129, 0.08), rgba(5, 150, 105, 0.08));
  border: 1px solid rgba(16, 185, 129, 0.3);
  position: relative;
  overflow: hidden;
}

.links-card .link-badge.inbound::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: linear-gradient(135deg, rgba(16, 185, 129, 0.02), rgba(5, 150, 105, 0.02));
  opacity: 0;
  transition: opacity 0.3s ease;
  pointer-events: none;
}

.links-card .link-badge.inbound:hover {
  background: linear-gradient(135deg, rgba(16, 185, 129, 0.15), rgba(5, 150, 105, 0.12));
  border-color: rgba(16, 185, 129, 0.5);
  transform: translateY(-3px);
  box-shadow: 0 8px 25px rgba(16, 185, 129, 0.2);
  border-left-color: #059669;
}

.links-card .link-badge.inbound:hover::before {
  opacity: 1;
}

.links-card .link-badge.inbound .badge-text {
  color: #047857;
  font-weight: 700;
  text-shadow: 0 1px 3px rgba(255, 255, 255, 0.8);
  position: relative;
  z-index: 1;
}

.links-card .link-badge.inbound .badge-source {
  color: #10b981;
  font-weight: 600;
  font-size: 0.65rem;
  background: rgba(16, 185, 129, 0.1);
  padding: 0.2rem 0.4rem;
  border-radius: 8px;
  backdrop-filter: blur(5px);
  border: 1px solid rgba(16, 185, 129, 0.2);
  margin-top: 0.4rem;
  position: relative;
  z-index: 1;
  text-align: center;
  display: block;
  width: fit-content;
  max-width: 100%;
}

.links-card .link-badge.outbound {
  border-left: 4px solid #667eea;
  background: linear-gradient(135deg, rgba(102, 126, 234, 0.08), rgba(118, 75, 162, 0.08));
  border: 1px solid rgba(102, 126, 234, 0.3);
  position: relative;
  overflow: hidden;
}

.links-card .link-badge.outbound::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: linear-gradient(135deg, rgba(102, 126, 234, 0.02), rgba(118, 75, 162, 0.02));
  opacity: 0;
  transition: opacity 0.3s ease;
  pointer-events: none;
}

.links-card .link-badge.outbound:hover {
  background: linear-gradient(135deg, rgba(102, 126, 234, 0.15), rgba(118, 75, 162, 0.12));
  border-color: rgba(102, 126, 234, 0.5);
  transform: translateY(-3px);
  box-shadow: 0 8px 25px rgba(102, 126, 234, 0.2);
  border-left-color: #5a67d8;
}

.links-card .link-badge.outbound:hover::before {
  opacity: 1;
}

.links-card .link-badge.outbound .badge-text {
  color: #4c51bf;
  font-weight: 700;
  text-shadow: 0 1px 3px rgba(255, 255, 255, 0.8);
  position: relative;
  z-index: 1;
}

.links-card .link-badge.outbound .badge-source {
  color: #667eea;
  font-weight: 600;
  font-size: 0.65rem;
  background: rgba(102, 126, 234, 0.1);
  padding: 0.2rem 0.4rem;
  border-radius: 8px;
  backdrop-filter: blur(5px);
  border: 1px solid rgba(102, 126, 234, 0.2);
  margin-top: 0.4rem;
  position: relative;
  z-index: 1;
  text-align: center;
  display: block;
  width: fit-content;
  max-width: 100%;
}

.links-card .link-badge .badge-text {
  font-weight: 600;
  color: #334155;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 100%;
  line-height: 1.2;
  text-shadow: 0 1px 2px rgba(255, 255, 255, 0.5);
}

.links-card .link-badge .badge-source {
  font-size: 0.7rem;
  color: #64748b;
  margin-top: 0.25rem;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 100%;
}

.links-card .no-links-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 2rem 1rem;
  text-align: center;
  color: #94a3b8;
  background: rgba(255, 255, 255, 0.05);
  backdrop-filter: blur(5px);
  border-radius: 12px;
  border: 1px dashed rgba(255, 255, 255, 0.2);
  margin: 1rem;
}

.links-card .no-links-state svg {
  margin-bottom: 0.75rem;
  opacity: 0.5;
}

.links-card .no-links-state span {
  font-size: 0.85rem;
  font-style: italic;
}

.save-status {
  position: fixed;
  top: 20px;
  right: 20px;
  padding: 12px 20px;
  border-radius: 12px;
  z-index: 10000;
  animation: slideInFromRight 0.3s ease-out;
  box-shadow: 0 8px 25px rgba(0, 0, 0, 0.15);
  display: flex;
  align-items: center;
  gap: 0.75rem;
  transition: all 0.3s ease;
  backdrop-filter: blur(10px);
  font-family: system-ui, -apple-system, sans-serif;
}

.save-status.saving {
  background: linear-gradient(135deg, rgba(255, 152, 0, 0.95), rgba(255, 193, 7, 0.95));
  color: white;
  border: 1px solid rgba(255, 152, 0, 0.3);
}

.save-status.saved {
  background: linear-gradient(135deg, rgba(76, 175, 80, 0.95), rgba(67, 160, 71, 0.95));
  color: white;
  border: 1px solid rgba(76, 175, 80, 0.3);
}

.save-status.error {
  background: linear-gradient(135deg, rgba(244, 67, 54, 0.95), rgba(211, 47, 47, 0.95));
  color: white;
  border: 1px solid rgba(244, 67, 54, 0.3);
}

.save-icon {
  width: 16px;
  height: 16px;
  flex-shrink: 0;
}

.save-icon.spinning {
  animation: spin 1s linear infinite;
}

.save-text {
  font-size: 14px;
  font-weight: 600;
  letter-spacing: 0.5px;
}

@keyframes slideInFromRight {
  from {
    transform: translateX(100%);
    opacity: 0;
  }
  to {
    transform: translateX(0);
    opacity: 1;
  }
}

@keyframes spin {
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
}

.notification.success {
  background-color: #4CAF50;
  color: white;
}

.notification.saved {
  background-color: #4CAF50;
  color: white;
}

.notification.saving {
  background-color: #ff9800;
  color: white;
}

.notification.error {
  background-color: #f44336;
  color: white;
}

.notification svg.spinning {
  animation: spin 1s linear infinite;
}

@keyframes spin {
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
}

.block-name-wrapper {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 0.75rem;
}

.block-id-container {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.block-id {
  font-size: 10px;
  color: #1d1c1c;
  font-weight: 500;
  font-family: 'Courier New', monospace;
  background: #f1f3f4;
  padding: 0.3rem 0.6rem;
  border-radius: 6px;
  border: 1px solid #d1d5db;
  letter-spacing: 0.5px;
  cursor: pointer;
}

.copy-id-btn {
  background: none;
  border: none;
  cursor: pointer;
  color: #6b7280;
  padding: 0.3rem;
  border-radius: 6px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s ease;
  opacity: 0.8;
}

.copy-id-btn:hover {
  background: #e5f3ff;
  color: #1EA355;
  opacity: 1;
  transform: scale(1.15);
}

.copy-id-btn svg {
  width: 13px;
  height: 13px;
}

/* Jarvis Toggle Switch Styles */
.jarvis-toggle {
  display: flex;
  align-items: center;
  margin-left: auto;
  padding-right: 16px;
}

.jarvis-controls {
  display: flex;
  align-items: center;
  gap: 12px;
}

.jarvis-label {
  display: flex;
  align-items: center;
  margin-right: 10px;
  font-size: 14px;
  color: #666;
  transition: color 0.3s ease;
}

.brain-icon {
  margin-right: 6px;
  color: #1abc73;
  transform: scale(0.9);
  opacity: 0.9;
  transition: color 0.3s ease;
}

.checkbox-wrapper-9 .tgl-flat:checked + .tgl-btn {
  border: 3px solid #7FC6A6;
}

.checkbox-wrapper-9 .tgl-flat:checked + .tgl-btn:after {
  background: #7FC6A6;
}

/* Dynamic color change based on jarvis status */
.jarvis-toggle[data-jarvis="true"] .jarvis-label,
.jarvis-toggle[data-jarvis="true"] .brain-icon {
  color: #7FC6A6;
}

.jarvis-knowledge-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  background: linear-gradient(135deg, #7FC6A6 0%, #667eea 100%);
  border-radius: 8px;
  color: white;
  text-decoration: none;
  transition: all 0.3s ease;
  box-shadow: 0 2px 8px rgba(127, 198, 166, 0.3);
}

.jarvis-knowledge-btn:hover {
  background: linear-gradient(135deg, #6bb394 0%, #5a6fd8 100%);
  transform: translateY(-2px);
  box-shadow: 0 4px 16px rgba(127, 198, 166, 0.4);
  color: white;
}

.jarvis-knowledge-btn svg {
  transition: transform 0.3s ease;
}

.jarvis-knowledge-btn:hover svg {
  transform: scale(1.1);
}

.checkbox-wrapper-9 .tgl {
  display: none;
}

.checkbox-wrapper-9 .tgl,
.checkbox-wrapper-9 .tgl:after,
.checkbox-wrapper-9 .tgl:before,
.checkbox-wrapper-9 .tgl *,
.checkbox-wrapper-9 .tgl *:after,
.checkbox-wrapper-9 .tgl *:before,
.checkbox-wrapper-9 .tgl + .tgl-btn {
  box-sizing: border-box;
}

.checkbox-wrapper-9 .tgl::-moz-selection,
.checkbox-wrapper-9 .tgl:after::-moz-selection,
.checkbox-wrapper-9 .tgl:before::-moz-selection,
.checkbox-wrapper-9 .tgl *::-moz-selection,
.checkbox-wrapper-9 .tgl *:after::-moz-selection,
.checkbox-wrapper-9 .tgl *:before::-moz-selection,
.checkbox-wrapper-9 .tgl + .tgl-btn::-moz-selection,
.checkbox-wrapper-9 .tgl::selection,
.checkbox-wrapper-9 .tgl:after::selection,
.checkbox-wrapper-9 .tgl:before::selection,
.checkbox-wrapper-9 .tgl *::selection,
.checkbox-wrapper-9 .tgl *:after::selection,
.checkbox-wrapper-9 .tgl *:before::selection,
.checkbox-wrapper-9 .tgl + .tgl-btn::selection {
  background: none;
}

.checkbox-wrapper-9 .tgl + .tgl-btn {
  outline: 0;
  display: block;
  width: 3em;
  height: 1.5em;
  position: relative;
  cursor: pointer;
  user-select: none;
}

.checkbox-wrapper-9 .tgl + .tgl-btn:after,
.checkbox-wrapper-9 .tgl + .tgl-btn:before {
  position: relative;
  display: block;
  content: "";
  width: 50%;
  height: 100%;
}

.checkbox-wrapper-9 .tgl + .tgl-btn:after {
  left: 0;
}

.checkbox-wrapper-9 .tgl + .tgl-btn:before {
  display: none;
}

.checkbox-wrapper-9 .tgl:checked + .tgl-btn:after {
  left: 50%;
}

.checkbox-wrapper-9 .tgl-flat + .tgl-btn {
  padding: 1px;
  transition: all 0.2s ease;
  background: #fff;
  border: 3px solid #f2f2f2;
  border-radius: 2em;
}

.checkbox-wrapper-9 .tgl-flat + .tgl-btn:after {
  transition: all 0.2s ease;
  background: #f2f2f2;
  content: "";
  border-radius: 1em;
}

.checkbox-wrapper-9 .tgl-flat:checked + .tgl-btn {
  border: 3px solid #7FC6A6;
}

.checkbox-wrapper-9 .tgl-flat:checked + .tgl-btn:after {
  left: 50%;
  background: #7FC6A6;
}

/* Create Variable Button Styles */
.create-variable-btn-link {
  text-decoration: none;
  display: block;
  width: 100%;
}

.create-variable-btn {
  background: rgba(255, 255, 255, 0.1);
  border: 1px solid rgba(255, 255, 255, 0.2);
  color: white;
  padding: 12px 16px;
  border-radius: 8px;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 8px;
  font-weight: 500;
  font-size: 14px;
  transition: all 0.2s ease;
  width: 100%;
  justify-content: flex-start;
}

.create-variable-btn:hover {
  background: rgba(255, 255, 255, 0.2);
  border-color: rgba(255, 255, 255, 0.3);
  transform: translateY(-1px);
}

.create-variable-btn svg {
  flex-shrink: 0;
}

.create-variable {
  margin-bottom: 20px;
}
</style> 